import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { userService } from '@/api/services/userService'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { toast } from '@/store/useToastStore'
import { useAuthStore } from '@/store/useAuthStore'
import { cn, DUMMY_AVATAR, onAvatarError, localized } from '@/shared/utils'
import { Spinner } from '@/shared/ui/Spinner'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { LocationMap } from '@/shared/ui/LocationMap'
import { AddressModal } from './AddressModal'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { packageService } from '@/api/services/packageService'
import { DocumentModal } from './DocumentModal'
import { DirhamAmount } from '@/shared/ui/Dirham'
import {
  Edit, Trash2, ChevronRight, ChevronLeft, Plus, Check, X, Copy,
  User, MapPin, Calendar, Star, Car, BookOpen,
  Clock, DollarSign, Shield, FileText, Building2, Trash, Download, Eye,
  Sun, Info, CalendarOff, CalendarPlus
} from 'lucide-react'

// ─── Tabs ──────────────────────────────────────────────────────────────────
const trainerTabs = (t: TFunction) => [
  { value: 'details',        label: t('userDetails.tabTrainerDetails') },
  { value: 'reviews',        label: t('userDetails.tabReviews')        },
  { value: 'packages',       label: t('userDetails.tabPackage')        },
  { value: 'working-hours',  label: t('userDetails.tabWorkingHours')   },
  { value: 'leave-days',     label: t('userDetails.tabLeaveDays')      },
  { value: 'students',       label: t('userDetails.tabStudents')       },
  { value: 'user-document',  label: t('userDetails.tabUserDocuments')  },
  { value: 'booking',        label: t('userDetails.tabBookings')       },
  { value: 'bank-details',   label: t('userDetails.tabBankDetails')    },
]
const studentTabs = (t: TFunction) => [
  { value: 'details',       label: t('userDetails.tabDetails')        },
  { value: 'booking',       label: t('userDetails.tabBookings')       },
  { value: 'review',        label: t('userDetails.tabReviews')        },
  { value: 'user-document', label: t('userDetails.tabUserDocuments')  },
]

// ─── Helpers ───────────────────────────────────────────────────────────────
const STATUS_KEY_MAP: Record<string, string> = {
  PENDING: 'status.pending', CONFIRMED: 'status.confirmed', COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled', 'IN PROGRESS': 'status.inProgress', REFUNDED: 'status.refunded',
  PAID: 'status.paid', FAILED: 'status.failed', ACTIVE: 'common.active', INACTIVE: 'common.inactive',
  'PARTIALLY PAID': 'userDetails.statusPartiallyPaid',
}
function statusLabel(t: TFunction, raw?: string | null) {
  if (!raw) return raw ?? '—'
  const key = STATUS_KEY_MAP[raw.toUpperCase()]
  return key ? t(key) : raw
}
function bookingChipClass(s: string) {
  const u = s?.toUpperCase()
  if (u === 'CONFIRMED')   return 'bg-brand-100 text-brand-700'
  if (u === 'CANCELLED')   return 'bg-red-100 text-red-600'
  if (u === 'IN PROGRESS') return 'bg-brand-100 text-brand-700'
  if (u === 'PENDING')     return 'bg-purple-100 text-purple-700'
  return 'bg-green-100 text-green-700'
}
function paymentChipClass(s: string) {
  const u = s?.toUpperCase()
  if (u === 'FAILED')         return 'bg-red-100 text-red-600'
  if (u === 'PENDING')        return 'bg-brand-100 text-brand-700'
  if (u === 'REFUNDED')       return 'bg-brand-100 text-brand-700'
  if (u === 'PARTIALLY PAID') return 'bg-indigo-100 text-indigo-700'
  return 'bg-green-100 text-green-700'
}
function fmt12(time: string) {
  // "08:30:00" or "2026-05-11 08:30:00" → "08:30 AM"
  const t = time?.includes(' ') ? time.split(' ')[1] : time
  if (!t) return '—'
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12  = h % 12 || 12
  return `${String(h12).padStart(2,'0')}:${String(m).padStart(2,'0')} ${ampm}`
}
function fmtLeaveDate(date: string) {
  // Handles both a plain "2026-01-15" and a full "2026-01-15T00:00:00+04:00" —
  // appending a time suffix to the latter would double up the "T" and fail to parse.
  const d = new Date(date.includes('T') ? date : `${date}T00:00:00`)
  return isNaN(d.getTime()) ? date : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

// ─── Pagination ────────────────────────────────────────────────────────────
function Pagination({ page, lastPage, total, perPage, onPageChange, onPerPageChange }: {
  page: number; lastPage: number; total: number; perPage: number
  onPageChange: (p: number) => void; onPerPageChange?: (n: number) => void
}) {
  const { t } = useTranslation()
  const from = (page - 1) * perPage + 1
  const to   = Math.min(page * perPage, total)
  const pages: (number | '…')[] = []
  if (lastPage <= 7) { for (let i=1;i<=lastPage;i++) pages.push(i) }
  else {
    pages.push(1)
    if (page > 3) pages.push('…')
    for (let i=Math.max(2,page-1);i<=Math.min(lastPage-1,page+1);i++) pages.push(i)
    if (page < lastPage-2) pages.push('…')
    pages.push(lastPage)
  }
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t border-gray-100 bg-white">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        {onPerPageChange && <>
          <span>{t('userDetails.itemsPerPage')}</span>
          <select value={perPage} onChange={e=>onPerPageChange(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400">
            {[5,10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
          </select>
        </>}
        <span>{t('userDetails.rangeOfTotal',{from,to,total:total.toLocaleString()})}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={()=>onPageChange(page-1)} disabled={page<=1}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 font-medium text-gray-600">
          <ChevronLeft size={13} className="rtl:rotate-180"/> {t('userDetails.previous')}
        </button>
        {pages.map((p,i)=>p==='…'
          ? <span key={`e${i}`} className="px-1 text-xs text-gray-400">…</span>
          : <button key={p} onClick={()=>onPageChange(p as number)}
              className={cn('w-8 h-8 text-xs rounded-lg border font-medium',
                p===page?'bg-brand-500 text-white border-brand-500':'border-gray-200 hover:bg-gray-50 text-gray-600')}>
              {p}
            </button>
        )}
        <button onClick={()=>onPageChange(page+1)} disabled={page>=lastPage}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 font-medium text-gray-600">
          {t('userDetails.next')} <ChevronRight size={13} className="rtl:rotate-180"/>
        </button>
      </div>
    </div>
  )
}

function Switch({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on}
      className={cn('relative inline-flex w-10 h-5 rounded-full transition-colors flex-shrink-0 disabled:opacity-50', on ? '' : 'bg-gray-300')}
      style={on ? { background:'#f68b2c' } : undefined}>
      <span className={cn('absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform', on && 'translate-x-5')} />
    </button>
  )
}

function InfoRow({ label, value, link, onClick }: {
  label: string; value?: React.ReactNode; link?: boolean; onClick?: ()=>void
}) {
  return (
    <div className="flex items-start py-1.5 border-b border-gray-50 last:border-0 gap-2">
      <span className="text-xs font-semibold text-gray-500 min-w-[200px] flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-xs text-gray-400 font-bold flex-shrink-0 pt-0.5">:</span>
      <span className={cn('text-xs flex-1 break-all', link?'text-brand-600 cursor-pointer hover:underline':'text-gray-800')} onClick={onClick}>
        {value??'—'}
      </span>
    </div>
  )
}

function SectionCard({ title, color='blue', icon, children, action }: {
  title: string; color?: string; icon?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode
}) {
  const hdr: Record<string,string> = {
    blue:'bg-brand-500', amber:'bg-brand-500', green:'bg-green-600',
    teal:'bg-teal-600', purple:'bg-purple-600', brown:'bg-[#795548]',
    red:'bg-red-500', gray:'bg-gray-600',
  }
  const bdr: Record<string,string> = {
    blue:'border-brand-200', amber:'border-brand-200', green:'border-green-200',
    teal:'border-teal-200', purple:'border-purple-200', brown:'border-[#a1887f]',
    red:'border-red-200', gray:'border-gray-200',
  }
  return (
    <div className={cn('rounded-xl border overflow-hidden shadow-sm', bdr[color]??'border-gray-200')}>
      <div className={cn('flex items-center justify-between px-4 py-2.5 text-white', hdr[color]??'bg-brand-500')}>
        <div className="flex items-center gap-2 text-sm font-bold">{icon}<span>{title}</span></div>
        {action}
      </div>
      <div className="bg-white p-4">{children}</div>
    </div>
  )
}

function StatusPill({ active }: { active: boolean }) {
  const { t } = useTranslation()
  return <span className={cn('px-3 py-0.5 rounded-full text-xs font-bold', active?'bg-green-500 text-white':'bg-red-100 text-red-600')}>{active?t('common.active'):t('common.inactive')}</span>
}
function Stars({ n }: { n: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({length:5},(_,i)=><span key={i} style={{color:i<Math.floor(n)?'#f68b2c':'#e5e7eb',fontSize:14}}>★</span>)}
      <span className="text-xs text-gray-400 ml-1">({Number(n).toFixed(1)})</span>
    </div>
  )
}
function CertBanner({ date }: { date: string }) {
  const { t } = useTranslation()
  const days = Math.floor((new Date(date).getTime()-Date.now())/86400000)
  const label = days>0?t('userDetails.daysRemaining',{n:days}):days===0?t('userDetails.expiresToday'):t('userDetails.expiredDaysAgo',{n:Math.abs(days)})
  const chip  = days<=0?'bg-red-100 text-red-700 border-red-300':days<=30?'bg-brand-100 text-brand-700 border-brand-300':'bg-green-100 text-green-700 border-green-300'
  const bg    = days<=0?'bg-red-50 border-red-200':days<=30?'bg-brand-50 border-brand-200':'bg-brand-50 border-brand-200'
  return (
    <div className={cn('flex items-center justify-between px-5 py-3 rounded-xl border mb-5',bg)}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center"><Calendar size={18} className="text-brand-600"/></div>
        <div>
          <div className="text-[12px] text-gray-500 font-semibold uppercase tracking-wide">{t('userDetails.certExpiryTitle')}</div>
          <div className="text-sm font-bold text-gray-900">{date}</div>
        </div>
      </div>
      <span className={cn('px-3 py-1 rounded-full text-xs font-semibold border',chip)}>{label}</span>
    </div>
  )
}
function Toggle({ on, onChange, disabled }: { on: boolean; onChange?: (next:boolean)=>void; disabled?: boolean }) {
  const body = (
    <div className={cn('w-10 h-5 rounded-full relative transition-colors flex-shrink-0',on?'bg-red-400':'bg-gray-300')}>
      <div className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all',on?'left-5':'left-0.5')}/>
    </div>
  )
  if (!onChange) return body
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled}
      onClick={()=>onChange(!on)}
      className={cn('flex-shrink-0', disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}>
      {body}
    </button>
  )
}

// ─── Student card (same as UserListPage StudentCard) ───────────────────────
function StudentCard({ user, onView }: { user: any; onView: ()=>void }) {
  const { t, i18n } = useTranslation()
  const booking   = user.latest_booking ?? user.bookings?.[0] ?? null
  const trainer   = booking?.trainer ?? user.trainer ?? null
  const pkg       = booking?.package ?? null
  const status    = booking?.booking_status ?? (user.is_active?'Active':'Inactive')
  const pkgName   = pkg?.name ?? pkg?.package_name ?? ''
  const userName    = localized(user, 'name', i18n.language)
  const trainerName = localized(trainer, 'name', i18n.language)
  const pkgPrice  = pkg?.price ?? booking?.total ?? null
  const statusStyles: Record<string,string> = {
    active:'bg-green-500 text-white', completed:'bg-brand-500 text-white',
    inactive:'bg-red-100 text-red-600', cancelled:'bg-red-500 text-white',
  }
  const statusStyle = statusStyles[status?.toLowerCase()] ?? 'bg-gray-100 text-gray-600'
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={user.photo_url || DUMMY_AVATAR} alt={userName} onError={onAvatarError}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0 bg-gray-100"/>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 text-sm truncate">{userName}</div>
            <div className="text-xs text-gray-400">{t('userDetails.studentIdPrefix',{id:String(user.id).padStart(4,'0')})}</div>
          </div>
        </div>
        <span className={cn('px-3 py-0.5 rounded-full text-xs font-semibold flex-shrink-0',statusStyle)}>{statusLabel(t,status)}</span>
      </div>
      <div className="px-4 pb-3 space-y-1">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="text-gray-400">✉</span>
          <span className="truncate">{user.email||'—'}</span>
          <span className="ml-auto">{user.phone||'—'}</span>
        </div>
        <div className="text-xs text-gray-400">
          {t('userDetails.bookingCreated',{date:user.created_at?new Date(user.created_at).toLocaleDateString('en-GB').replace(/\//g,'-'):'—'})}
          {user.city?.name && <span className="ms-3">📍 {user.city.name}</span>}
        </div>
      </div>
      {trainer && (
        <div className="mx-4 mb-3 rounded-xl bg-brand-50">
          <div className="px-3 py-1.5 text-xs font-bold text-gray-600 border-b border-brand-100">{t('userDetails.trainerInfoTitle')}</div>
          <div className="px-3 py-2">
            <div className="text-sm font-semibold text-gray-800">{trainerName}</div>
            {trainer?.phone&&<div className="text-xs text-gray-500">{trainer.phone}</div>}
          </div>
        </div>
      )}
      {pkgName&&(
        <div className="mx-4 mb-3 rounded-xl overflow-hidden border border-gray-100">
          <div className="px-3 py-1.5 text-xs font-bold text-gray-600 bg-gray-50 border-b border-gray-100">{t('userDetails.paymentInfoTitle')}</div>
          <div className="px-3 py-2.5 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">{pkgName}</span>
            {pkgPrice!=null&&<DirhamAmount value={pkgPrice}/>}
          </div>
        </div>
      )}
      <div className="px-4 pb-4">
        <button onClick={onView} className="w-full py-2 rounded-xl border-2 text-sm font-semibold hover:opacity-80 transition-all" style={{borderColor:'#f68b2c',color:'#f68b2c'}}>
          {t('userDetails.viewDetailsBtn')}
        </button>
      </div>
    </div>
  )
}

// ─── Booking card (trainer view) ────────────────────────────────────────────
function BookingCard({ booking, onClick, onTrainerClick }: { booking: any; onClick: ()=>void; onTrainerClick?: (tid:number|string)=>void }) {
  const { t, i18n } = useTranslation()
  const bookingUserName = localized(booking?.user, 'name', i18n.language)
  // Trainer on the booking — API: driver{} (or driver_id when the relation isn't expanded)
  const trainerId   = booking?.driver?.id ?? booking?.driver_id
  const trainerName = booking?.driver ? localized(booking.driver, 'name', i18n.language) : '—'
  // Session counts — API: no_of_sessions (total) / booked_session / no_of_sessions_completed
  // Unlimited packages are flagged by is_unlimited OR a -1 (negative) session sentinel
  const rawTotal = Number(booking?.no_of_sessions ?? booking?.package?.number_of_sessions ?? 0)
  const isUnlimited = !!(booking?.package?.is_unlimited ?? booking?.is_unlimited) || rawTotal < 0
  const totalSessions = isUnlimited ? 0 : rawTotal
  const bookedSessions = Number(booking?.booked_session ?? 0)
  const doneSessions = Number(booking?.no_of_sessions_completed ?? 0)
  // Clamp to 0–100 so malformed counts can't produce values like -2200%
  const progress = totalSessions > 0
    ? Math.min(100, Math.max(0, Math.round((doneSessions / totalSessions) * 100)))
    : 0
  const initials = (bookingUserName!=='—'?bookingUserName:'NA').split(' ').map((n:string)=>n[0]).join('').toUpperCase().slice(0,2)
  return (
    <div onClick={onClick} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer p-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-[200px] flex-shrink-0">
          <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{initials}</div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{bookingUserName}</div>
            <div className="text-xs text-gray-400 truncate">{booking?.user?.email??'—'}</div>
            {booking?.created_at && (
              <div className="text-[12px] text-gray-400 flex items-center gap-1 mt-0.5">
                <Calendar size={10}/>
                {new Date(String(booking.created_at).replace(' ','T')).toLocaleString('en-US',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
              </div>
            )}
          </div>
        </div>
        {trainerId && (
          <div className="flex-shrink-0 min-w-[130px]">
            <div className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('userDetails.trainerLabel')}</div>
            <button type="button"
              onClick={e=>{e.stopPropagation(); onTrainerClick?.(trainerId)}}
              disabled={!onTrainerClick}
              className="text-sm font-semibold text-brand-600 hover:text-brand-700 hover:underline truncate max-w-[130px] text-start disabled:text-gray-900 disabled:no-underline disabled:cursor-default">
              {trainerName}
            </button>
          </div>
        )}
        <div className="flex-shrink-0 min-w-[100px]">
          <div className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('userDetails.amountLabel')}</div>
          <DirhamAmount value={booking?.total} textClass="text-xl font-bold text-gray-900" size={14}/>
        </div>
        <div className="flex gap-2 flex-1 min-w-[200px]">
          {[{label:t('userDetails.totalStat'),value:isUnlimited?'∞':totalSessions,bg:'bg-gray-50 border border-gray-200'},
            {label:t('userDetails.bookedStat'),value:bookedSessions,bg:'bg-gray-50 border border-gray-200'},
            {label:t('userDetails.doneStat'),value:doneSessions,bg:'bg-gray-900'}].map((s,i)=>(
            <div key={i} className={cn('flex-1 rounded-xl p-2 text-center',s.bg)}>
              <div className={cn('text-[11px] font-bold uppercase tracking-wide mb-0.5',i===2?'text-gray-300':'text-gray-500')}>{s.label}</div>
              <div className={cn('text-xl font-black',i===2?'text-white':'text-gray-900')}>{s.value??'—'}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1.5 min-w-[160px] flex-shrink-0">
          <div>
            <div className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('userDetails.bookingStatusLabel')}</div>
            <span className={cn('inline-block w-full text-center px-2 py-1 rounded-lg text-xs font-bold',bookingChipClass(booking?.booking_status))}>{statusLabel(t,booking?.booking_status)}</span>
          </div>
          <div>
            <div className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">{t('userDetails.paymentStatusLabel')}</div>
            <span className="inline-block w-full text-center px-2 py-1 rounded-lg text-xs font-bold border-2 border-[#f68b2c] text-[#f68b2c] bg-white">{statusLabel(t,booking?.payment_status)}</span>
          </div>
        </div>
        {isUnlimited ? (
          <div className="flex-shrink-0 w-16 h-16 rounded-full border-[5px] border-gray-900 flex items-center justify-center text-center">
            <span className="text-[11px] font-bold text-gray-900 leading-tight px-1">{t('userDetails.unlimitedBadge')}</span>
          </div>
        ) : (
          <div className="flex-shrink-0 w-16 h-16 relative">
            <svg width="64" height="64" style={{transform:'rotate(-90deg)'}}>
              <circle cx="32" cy="32" r="26" fill="none" stroke="#E5E7EB" strokeWidth="5"/>
              <circle cx="32" cy="32" r="26" fill="none" stroke="#111827" strokeWidth="5"
                strokeDasharray={2*Math.PI*26} strokeDashoffset={2*Math.PI*26*(1-progress/100)} strokeLinecap="round"/>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-900">{progress}%</div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── MAIN ──────────────────────────────────────────────────────────────────
export function UserDetailsPage() {
  const { t, i18n }   = useTranslation()
  const confirmAction = useConfirm()
  const { id }        = useParams<{id:string}>()
  const navigate      = useNavigate()
  const location      = useLocation()
  // When opened from the school-admin panel, keep navigation inside /school so the
  // school sidebar (not the super-admin one) stays in context.
  const isSchool      = location.pathname.startsWith('/school')
  const listPath      = isSchool ? '/school/trainers-list' : '/dashboard/user/list'
  const userPath      = (uid: string | number) => isSchool ? `/school/trainer/${uid}` : `/dashboard/user/${uid}`
  const bookingPath   = (bid: string | number) => isSchool ? `/school/booking/${bid}` : `/dashboard/booking/${bid}`
  // Lets a link elsewhere (e.g. the "Bookings" tag on the user list) open this
  // page straight to a specific tab via ?tab=booking, instead of always "details".
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'details')

  // pagination state
  const [bookingPage, setBookingPage]       = useState(1)
  const [bookingPerPage, setBookingPerPage] = useState(15)
  const [reviewPage, setReviewPage]         = useState(1)
  const [reviewPerPage, setReviewPerPage]   = useState(10)
  const [studentPage, setStudentPage]       = useState(1)
  const [studentPerPage, setStudentPerPage] = useState(6)
  const [bookingPayStatus, setBookingPayStatus] = useState('')
  const [bookingPayMethod, setBookingPayMethod] = useState('')

  // React Router reuses this same component instance when navigating between
  // two /user/:id views (e.g. clicking a student from a trainer's "Students"
  // tab) — it doesn't remount, so `tab` would otherwise stay on whatever was
  // selected before. If that happens to be a trainer-only tab like "students"
  // and the newly-loaded user is a student (whose TABS don't include it),
  // nothing matches and the page renders blank. Reset per-record view state
  // whenever the id in the URL actually changes.
  useEffect(() => {
    setTab(new URLSearchParams(location.search).get('tab') || 'details')
    setBookingPage(1)
    setReviewPage(1)
    setStudentPage(1)
  }, [id])

  // ── Main user query ──
  const { data: raw, isLoading } = useQuery({
    queryKey: ['users', Number(id)],
    queryFn: ()=>userService.getById(Number(id)),
    enabled: !!id,
  })
  const details: any = (raw as any)?.data ?? raw ?? {}
  const detailsName   = localized(details, 'name', i18n.language)
  const currentUser  = useAuthStore((s) => s.user)
  const isSchoolAdmin= currentUser?.user_type === 'school_admin'
  const isTrainer    = details?.user_type === 'TRAINER'
  const typeTitle    = isTrainer ? t('userDetails.trainerDetailsTitle') : t('userDetails.userDetailsTitle')
  const infoTitle    = isTrainer ? t('userDetails.trainerInfoTitle') : t('userDetails.userInfoTitle')
  const idLabel      = isTrainer ? t('userDetails.trainerIdLabel') : t('userDetails.userIdLabel')
  const TABS         = isTrainer ? trainerTabs(t) : studentTabs(t)
  const pref         = details?.user_preference ?? {}

  // ── Verify trainer — uses the user-update-by-id endpoint ──
  const verifyQc = useQueryClient()
  const [verifying, setVerifying] = useState(false)

  // ── Address add/edit modal ──
  const [addressModal, setAddressModal] = useState<{ open: boolean; address: any | null }>({ open: false, address: null })
  const [showAllAddresses, setShowAllAddresses] = useState(false)
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null)
  const [copiedAddrId, setCopiedAddrId] = useState<number | string | null>(null)
  const handleCopyLocation = (addr: any) => {
    if (addr?.latitude == null || addr?.longitude == null) return
    navigator.clipboard.writeText(`https://www.google.com/maps?q=${addr.latitude},${addr.longitude}`)
    setCopiedAddrId(addr?.id ?? true)
    setTimeout(() => setCopiedAddrId(null), 2000)
  }

  // ── Document add/edit modal ──
  const [documentModal, setDocumentModal] = useState<{ open: boolean; doc: any | null }>({ open: false, doc: null })

  // ── Document preview (lightbox) ──
  const [docPreview, setDocPreview] = useState<any | null>(null)
  const isPdf = (url?: string) => !!url && /\.pdf(\?|$)/i.test(url)
  const downloadDoc = async (url?: string, name = 'document') => {
    if (!url) return
    try {
      const res = await fetch(url)
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = name + (isPdf(url) ? '.pdf' : (url.match(/\.(png|jpe?g|webp|gif)(\?|$)/i)?.[1] ? `.${url.match(/\.(png|jpe?g|webp|gif)(\?|$)/i)![1]}` : ''))
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(href)
    } catch {
      window.open(url, '_blank') // S3/CORS fallback
    }
  }

  const [deletingAddressId, setDeletingAddressId] = useState<number|null>(null)
  const handleDeleteAddress = async (addr: any) => {
    if (!(await confirmAction({
      title: t('userDetails.deleteAddressTitle'),
      description: t('userDetails.deleteAddressDescription'),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    }))) return
    setDeletingAddressId(addr?.id)
    try {
      await userService.deleteAddress(addr.id)
      toast.success(t('userDetails.addressDeleted'))
      await verifyQc.invalidateQueries({ queryKey: ['users', Number(id)] })
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('userDetails.failedDeleteAddress'))
    } finally {
      setDeletingAddressId(null)
    }
  }

  // Quick "set as default" — only sends { id, is_default: 1 } to the same address endpoint
  const handleSetDefaultAddress = async (addr: any) => {
    setSettingDefaultId(addr?.id)
    try {
      await userService.saveAddress({ id: addr.id, is_default: 1 })
      toast.success(t('userDetails.defaultAddressUpdated'))
      await verifyQc.invalidateQueries({ queryKey: ['users', Number(id)] })
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('userDetails.failedSetDefaultAddress'))
    } finally {
      setSettingDefaultId(null)
    }
  }

  const handleVerify = async (verify: boolean) => {
    setVerifying(true)
    try {
      const body = new FormData()
      body.append('user_id', String(details?.id))
      body.append('is_verified', verify ? '1' : '0')
      await userService.update(body as any)
      await verifyQc.invalidateQueries({ queryKey: ['users', Number(id)] })
      toast.success(verify ? t('userDetails.trainerVerified') : t('userDetails.trainerUnverified'))
    } catch (e: any) {
      const errs = e?.response?.data?.errors
      const msg  = errs
        ? (typeof errs === 'string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? t('userDetails.failedUpdateVerification'))
      toast.error(msg)
    } finally {
      setVerifying(false)
    }
  }

  // ── Admin suspend toggle — same user-update-by-id endpoint ──
  const [suspending, setSuspending] = useState(false)
  const handleSuspend = async (suspend: boolean) => {
    if (!(await confirmAction({
      title: suspend ? t('userDetails.suspendConfirmTitle') : t('userDetails.unsuspendConfirmTitle'),
      description: suspend ? t('userDetails.suspendConfirmDescription') : t('userDetails.unsuspendConfirmDescription'),
      confirmLabel: suspend ? t('userDetails.suspend') : t('userDetails.unsuspend'),
      variant: suspend ? 'danger' : 'primary',
    }))) return
    setSuspending(true)
    try {
      const body = new FormData()
      body.append('user_id', String(details?.id))
      body.append('is_suspended', suspend ? '1' : '0')
      await userService.update(body as any)
      await verifyQc.invalidateQueries({ queryKey: ['users', Number(id)] })
      toast.success(suspend ? t('userDetails.userSuspended') : t('userDetails.userUnsuspended'))
    } catch (e: any) {
      const errs = e?.response?.data?.errors
      toast.error(errs
        ? (typeof errs === 'string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? t('userDetails.failedUpdateSuspension')))
    } finally {
      setSuspending(false)
    }
  }

  // vendor_id for the school/vendor package list query
  const vendorId = details?.vendor?.id

  // ── Reviews — exact API shape: data[0].reviews[], data[0].avg_rating, data[0].rating_breakdown ──
  const { data: reviewsRaw } = useQuery({
    queryKey: ['reviews','trainer',id,reviewPage,reviewPerPage],
    queryFn: ()=>restClient.get(
      `${endpoints.review.getTrainerReview}?trainer_id=${id}&page=${reviewPage}&limit=${reviewPerPage}`
    ).then(r=>r.data),
    enabled: !!id && tab==='reviews',
  })
  const reviewTrainer: any    = (reviewsRaw as any)?.data?.[0] ?? null
  const reviews: any[]         = reviewTrainer?.reviews ?? []
  const reviewAvg: number      = reviewTrainer?.avg_rating ?? 0
  const reviewBreakdown: any   = reviewTrainer?.rating_breakdown ?? {}
  const reviewCount: number    = reviewTrainer?.rating_count ?? 0
  const reviewTotal            = (reviewsRaw as any)?.total ?? reviews.length
  const reviewLastPage         = Math.max(1, Math.ceil(reviewTotal/reviewPerPage))

  // ── Delete review (student comment) — admin/reviews/delete-review?delete_student_comment=1&session_id=X ──
  const [reviewDeleteTarget, setReviewDeleteTarget] = useState<any|null>(null)
  const [reviewDeleting, setReviewDeleting] = useState(false)

  const confirmDeleteReview = async () => {
    if (!reviewDeleteTarget) return
    setReviewDeleting(true)
    try {
      await restClient.post(`${endpoints.review.deleteReview}delete_student_comment=1&session_id=${reviewDeleteTarget.session_id}`, {})
      toast.success(t('userDetails.reviewDeleted'))
      setReviewDeleteTarget(null)
      await verifyQc.invalidateQueries({ queryKey: ['reviews','trainer',id] })
    } catch (e:any) {
      const errs = e?.response?.data?.errors
      toast.error(errs
        ? (typeof errs==='string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? t('userDetails.failedDeleteReview')))
    } finally {
      setReviewDeleting(false)
    }
  }

  // ── Package modal state ──────────────────────────────────────────────────
  const [pkgModal,    setPkgModal]    = useState<{open:boolean; mode:'add'|'edit'; pkg:any|null}>({open:false,mode:'add',pkg:null})
  const [pkgForm,     setPkgForm]     = useState({ package_id:'', trainer_price:'', status:false, is_published:false })
  const [pkgErrors,   setPkgErrors]   = useState<Record<string,string>>({})
  const [pkgSaving,   setPkgSaving]   = useState(false)
  const [pkgDeleting, setPkgDeleting] = useState<number|null>(null)
  const pkgQc = useQueryClient()

  // ── Packages assigned to trainer — comes from the main user-details response (details.packages)
  const packages: any[] = details?.packages ?? []
  const pkgLoading = isLoading

  // ── Available packages for dropdown ──
  // School admin: the school/vendor package list. Super admin: the trainer-scoped
  // list (get-trainer-packages/{trainer_user_id}).
  // Trainers without a vendor assigned yet fall back to vendor_id 6 so the
  // dropdown still has packages to offer instead of coming back empty.
  const pkgVendorId = vendorId ?? 6
  const { data: availPkgRaw, isFetching: availPkgLoading } = useQuery({
    queryKey: ['packages','available', isSchoolAdmin ? `school-${vendorId}` : `vendor-${pkgVendorId}`],
    queryFn: () => isSchoolAdmin
      ? restClient.get(`${endpoints.school.package.getPackageBySchool}?school_id=${vendorId}&locale=en`).then(r=>r.data)
      // Super admin: every published package for this trainer's own vendor, not
      // just the ones already mapped to them (get-trainer-packages returns the
      // existing mapping only).
      : packageService.list({ page: 1, limit: 500, is_published: 1, vendor_id: pkgVendorId }),
    enabled: pkgModal.open && pkgModal.mode==='add' && (isSchoolAdmin ? !!vendorId : true),
    staleTime: 30_000,
  })
  // Rows may wrap the package under `package`; unwrap so the dropdown/validation
  // see the same shape either way, and drop anything unpublished/inactive.
  const availPackages: any[] = ((availPkgRaw as any)?.data ?? [])
    .map((r:any) => r?.package ?? r)
    .filter((p:any) => p && p.id != null
      && p.is_published !== 0 && p.is_published !== false
      && p.is_active !== 0 && p.is_active !== false
      && p.status !== 0 && p.status !== 'inactive')
    // The same package can appear more than once across vendor rows.
    .filter((p:any, i:number, arr:any[]) => arr.findIndex(x => String(x.id) === String(p.id)) === i)

  // ── Package theme colours ──────────────────────────────────────────────────
  const pkgBgMap: Record<string,{bg:string; text:string; border:string; check:string}> = {
    bronze:    { bg:'#fdf3e7', text:'#7c3d12', border:'#e8c49a', check:'#d97706' },
    silver:    { bg:'#f4f4f5', text:'#3f3f46', border:'#d4d4d8', check:'#71717a' },
    gold:      { bg:'#fffbeb', text:'#713f12', border:'#fcd34d', check:'#d97706' },
    unlimited: { bg:'#f5f3ff', text:'#4c1d95', border:'#c4b5fd', check:'#7c3aed' },
    trial:     { bg:'#eff6ff', text:'#1e3a8a', border:'#bfdbfe', check:'#3b82f6' },
    express:   { bg:'#fef2f2', text:'#7f1d1d', border:'#fca5a5', check:'#dc2626' },
    red:       { bg:'#fef2f2', text:'#7f1d1d', border:'#fca5a5', check:'#dc2626' },
  }
  const pkgTheme = (bg: string) => pkgBgMap[bg?.toLowerCase()] ?? pkgBgMap.trial

  // ── Package CRUD helpers ───────────────────────────────────────────────────
  const openAddPkg = () => {
    setPkgForm({ package_id:'', trainer_price:'', status:false, is_published:false })
    setPkgErrors({})
    setPkgModal({ open:true, mode:'add', pkg:null })
  }
  const openEditPkg = (pkg: any) => {
    setPkgForm({
      package_id:    String(pkg.package_id ?? pkg.package?.id ?? ''),
      trainer_price: String(pkg.price ?? ''),
      status:        pkg.status === 1,
      is_published:  pkg.is_published === 1,
    })
    setPkgErrors({})
    setPkgModal({ open:true, mode:'edit', pkg })
  }
  const validatePkg = () => {
    const errs: Record<string,string> = {}
    if (!pkgForm.package_id && pkgModal.mode==='add') errs.package_id = t('userDetails.packageRequired')
    if (!pkgForm.trainer_price) {
      errs.trainer_price = t('userDetails.priceRequired')
    } else {
      const sel = availPackages.find(p => String(p.id) === String(pkgForm.package_id))
      const cityPrice = sel?.package_city?.[0]
      if (cityPrice) {
        const v = Number(pkgForm.trainer_price)
        if (v < Number(cityPrice.min_price) || v > Number(cityPrice.max_price))
          errs.trainer_price = t('userDetails.priceBetween',{min:cityPrice.min_price,max:cityPrice.max_price})
      }
    }
    return errs
  }
  const submitPkg = async () => {
    const errs = validatePkg()
    if (Object.keys(errs).length) { setPkgErrors(errs); return }
    if (pkgModal.mode==='edit' && !(await confirmAction({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    setPkgSaving(true)
    try {
      const body = {
        package_id: pkgModal.mode==='edit' ? (pkgModal.pkg?.package_id ?? pkgModal.pkg?.package?.id) : pkgForm.package_id,
        trainer_ids: [{ id, price: Number(pkgForm.trainer_price), status: pkgForm.status?1:0, is_published: pkgForm.is_published?1:0 }],
      }
      await restClient.post(endpoints.packageTrainer.create, body)
      pkgQc.invalidateQueries({ queryKey: ['users', Number(id)] })
      setPkgModal({ open:false, mode:'add', pkg:null })
    } catch(e:any) {
      setPkgErrors({ submit: e?.response?.data?.message ?? t('userDetails.failedToSave') })
    } finally { setPkgSaving(false) }
  }
  const deletePkg = async (pkgId: number) => {
    if (!(await confirmAction({
      title: t('common.confirmDeleteTitle'), description: t('userDetails.removePackageConfirm'),
      confirmLabel: t('common.remove'),
    }))) return
    setPkgDeleting(pkgId)
    try {
      await restClient.delete(`${endpoints.packageTrainer.delete}/${pkgId}`)
      pkgQc.invalidateQueries({ queryKey: ['users', Number(id)] })
    } catch {} finally { setPkgDeleting(null) }
  }

  // ── Shifts — exact API: data[].shift_start_time, shift_end_time ──
  const { data: shiftsRaw } = useQuery({
    queryKey: ['shifts','trainer',id],
    queryFn: ()=>restClient.get(
      `${endpoints.trainer.shift.get}?trainer_id=${id}`
    ).then(r=>r.data),
    enabled: !!id && tab==='working-hours',
  })
  const shifts: any[] = (shiftsRaw as any)?.data ?? []

  // ── Working hours — API: get-by-user-id?user_id=X&limit=10000 → data[].day_of_week, start_time, end_time, is_off_day, is_full_day ──
  const { data: workingHoursRaw } = useQuery({
    queryKey: ['working-hours','trainer',id],
    queryFn: ()=>restClient.get(
      `${endpoints.trainer.workingHours.getList}?user_id=${id}&limit=10000`
    ).then(r=>r.data),
    enabled: !!id && tab==='working-hours',
  })
  const workingHours: any[] = (workingHoursRaw as any)?.data ?? []
  // group by day_of_week
  const whByDay: Record<string,any[]> = {}
  workingHours.forEach((wh:any)=>{
    const d = wh.day_of_week
    if (!whByDay[d]) whByDay[d] = []
    whByDay[d].push(wh)
  })
  const DAYS = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY']

  // ── Leave days — API: get-leave-list?trainer_id=X&page&limit → data[].date, is_full_day_off ──
  // Fetched once (a trainer only ever has a handful of these) and shown as a
  // plain chronological list — no calendar grid on this view.
  const { data: leaveRaw, isLoading: leaveLoading } = useQuery({
    queryKey: ['leave-days','trainer',id],
    queryFn: ()=>restClient.get(
      `${endpoints.trainer.leave.list}?trainer_id=${id}&page=1&limit=1000`
    ).then(r=>r.data),
    enabled: !!id && tab==='leave-days',
  })
  const leaveDays: any[] = (leaveRaw as any)?.data ?? []
  const leaveTotal: number = (leaveRaw as any)?.total ?? leaveDays.length
  const sortedLeaveDays = useMemo(
    () => [...leaveDays].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')),
    [leaveDays]
  )
  const todayISO = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` }, [])

  // ── Add/edit a leave day — POST create-update-leave { id, trainer_id, dates[], is_full_day_off, start_time, end_time } ──
  const [leaveModal,      setLeaveModal]      = useState(false)
  const [leaveEditing,    setLeaveEditing]    = useState<any | null>(null)
  const [leaveRangeStart, setLeaveRangeStart] = useState('')
  const [leaveRangeEnd,   setLeaveRangeEnd]   = useState('')
  const [leaveDateList,   setLeaveDateList]   = useState<string[]>([])
  const [leaveFullDay,    setLeaveFullDay]    = useState(true)
  const [leaveStartTime,  setLeaveStartTime]  = useState('')
  const [leaveEndTime,    setLeaveEndTime]    = useState('')
  const [leaveSaving,     setLeaveSaving]     = useState(false)
  const [leaveDeleting,   setLeaveDeleting]   = useState<string | null>(null)

  const openAddLeave = (presetDate?: string) => {
    setLeaveEditing(null)
    setLeaveDateList(presetDate ? [presetDate] : [])
    setLeaveRangeStart(presetDate ?? ''); setLeaveRangeEnd(presetDate ?? '')
    setLeaveFullDay(true); setLeaveStartTime(''); setLeaveEndTime('')
    setLeaveModal(true)
  }
  const openEditLeave = (l: any) => {
    setLeaveEditing(l)
    setLeaveDateList([(l.date ?? '').slice(0, 10)].filter(Boolean))
    setLeaveRangeStart(''); setLeaveRangeEnd('')
    setLeaveFullDay(!!l.is_full_day_off)
    setLeaveStartTime((l.start_time ?? '').slice(0, 5))
    setLeaveEndTime((l.end_time ?? '').slice(0, 5))
    setLeaveModal(true)
  }
  // Expands the picked From/To range into individual "YYYY-MM-DD" strings and
  // merges them into the date list — a single day is just From === To.
  const addLeaveRange = () => {
    if (!leaveRangeStart || !leaveRangeEnd) return
    const start = new Date(`${leaveRangeStart}T00:00:00`)
    const end   = new Date(`${leaveRangeEnd}T00:00:00`)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return
    if (end < start) { toast.error(t('userDetails.leaveRangeOrder')); return }
    const spanDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1
    if (spanDays > 90) { toast.error(t('userDetails.leaveRangeTooLong')); return }
    const added: string[] = []
    for (let i = 0; i < spanDays; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i)
      added.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`)
    }
    setLeaveDateList(prev => Array.from(new Set([...prev, ...added])).sort())
    setLeaveRangeStart(''); setLeaveRangeEnd('')
  }
  const removeLeaveDate = (d: string) => setLeaveDateList(prev => prev.filter(x => x !== d))

  // DELETE auth/leave/delete-leave-by-dates?dates=YYYY-MM-DD,YYYY-MM-DD
  const deleteLeaveDates = async (dates: string[]) => {
    if (dates.length === 0) return
    if (!(await confirmAction({
      title: t('common.confirmDeleteTitle'),
      description: dates.length > 1 ? t('userDetails.deleteLeaveRangeConfirm', { count: dates.length }) : t('userDetails.deleteLeaveConfirm'),
      confirmLabel: t('common.remove'),
    }))) return
    setLeaveDeleting(dates[0])
    try {
      await restClient.delete(`${endpoints.trainer.leave.deleteByDates}?dates=${dates.join(',')}&trainer_id=${id}`)
      toast.success(t('userDetails.leaveDeleted'))
      pkgQc.invalidateQueries({ queryKey: ['leave-days', 'trainer', id] })
      setLeaveModal(false)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('userDetails.failedToDeleteLeave'))
    } finally { setLeaveDeleting(null) }
  }

  const submitLeave = async () => {
    if (leaveDateList.length === 0) { toast.error(t('userDetails.leaveDatesRequired')); return }
    if (!leaveFullDay && (!leaveStartTime || !leaveEndTime)) { toast.error(t('userDetails.leaveTimesRequired')); return }
    setLeaveSaving(true)
    try {
      await restClient.post(endpoints.trainer.leave.createUpdate, {
        id: leaveEditing?.id ?? 0,
        trainer_id: Number(id),
        dates: leaveDateList,
        is_full_day_off: leaveFullDay,
        start_time: leaveFullDay ? '' : leaveStartTime,
        end_time: leaveFullDay ? '' : leaveEndTime,
      })
      toast.success(leaveEditing ? t('userDetails.leaveUpdated') : t('userDetails.leaveAdded'))
      setLeaveModal(false)
      pkgQc.invalidateQueries({ queryKey: ['leave-days', 'trainer', id] })
    } catch (e: any) {
      const errs = e?.response?.data?.errors
      toast.error(errs
        ? (typeof errs === 'string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? t('userDetails.failedToSaveLeave')))
    } finally { setLeaveSaving(false) }
  }

  // ── Students ──
  const { data: studentsRaw, isLoading: studentsLoading } = useQuery({
    queryKey: ['students','trainer',id,studentPage,studentPerPage,isSchoolAdmin],
    queryFn: ()=>restClient.get(
      `${isSchoolAdmin ? endpoints.users.studentsBySchool : endpoints.users.students}?page=${studentPage}&limit=${studentPerPage}&sort_dir=desc&trainer_id=${id}`
    ).then(r=>r.data),
    enabled: !!id && tab==='students',
  })
  // API shape: data[] (rows) with pagination either under meta{} or at the root (total/last_page).
  const studentsRoot     = (studentsRaw as any)?.data
  const students: any[]  = Array.isArray(studentsRoot) ? studentsRoot
    : Array.isArray(studentsRoot?.data) ? studentsRoot.data
    : []
  const studentTotal     = (studentsRaw as any)?.meta?.total ?? (studentsRaw as any)?.total
    ?? studentsRoot?.meta?.total ?? studentsRoot?.total ?? students.length
  const studentLastPage  = (studentsRaw as any)?.meta?.last_page ?? (studentsRaw as any)?.last_page
    ?? studentsRoot?.meta?.last_page ?? studentsRoot?.last_page
    ?? Math.max(1, Math.ceil(studentTotal / studentPerPage))
  const studentMeta: any = { total: studentTotal, last_page: studentLastPage }

  // ── Bookings ──
  const { data: bookingsRaw, isLoading: bookingsLoading } = useQuery({
    queryKey: ['bookings',isTrainer?'trainer':'student',id,bookingPage,bookingPerPage,bookingPayStatus,bookingPayMethod],
    // Trainers list bookings by driver_id; students use the by-student-id endpoint.
    queryFn: ()=>restClient.get(
      (isTrainer
        ? `${endpoints.booking.getList}?driver_id=${id}&limit=${bookingPerPage}&page=${bookingPage}`
        : `${endpoints.booking.getBookingById}?student_id=${id}&limit=${bookingPerPage}&page=${bookingPage}`) +
      (bookingPayStatus?`&payment_status=${bookingPayStatus}`:'') +
      (bookingPayMethod?`&payment_method=${bookingPayMethod}`:'')
    ).then(r=>r.data),
    enabled: !!id && tab==='booking',
  })
  const bookingsRoot     = (bookingsRaw as any)?.data
  // API shape: data.bookings[] (with total at the root); fall back to data[] / data.data[] for older shapes
  const bookings: any[]  = Array.isArray(bookingsRoot) ? bookingsRoot
    : Array.isArray(bookingsRoot?.bookings) ? bookingsRoot.bookings
    : Array.isArray(bookingsRoot?.data) ? bookingsRoot.data
    : []
  const bookingTotal     = (bookingsRaw as any)?.total ?? bookingsRoot?.total ?? (bookingsRaw as any)?.meta?.total ?? bookingsRoot?.meta?.total ?? bookings.length
  const bookingMeta: any = { total: bookingTotal, last_page: Math.max(1, Math.ceil(bookingTotal / bookingPerPage)) }
  const totalRevenue  = bookings.reduce((s:number,b:any)=>s+(Number(b.total)||0),0)
  const totalSessions = bookings.reduce((s:number,b:any)=>s+Math.max(0,Number(b.no_of_sessions ?? b.package?.number_of_sessions)||0),0)
  const totalDone     = bookings.reduce((s:number,b:any)=>s+(Number(b.no_of_sessions_completed)||0),0)


  // ── Bank & docs ──
  const bankDetails: any[]= details?.bank_detail ?? []
  const docs: any[]        = details?.user_docs ?? []
  const addresses: any[]   = details?.user_addresses ?? []
  const avgRating          = details?.avg_rating ?? details?.average_rating ?? null

  // derived user_preference fields
  const vehicle  = pref?.vehicle_type?.category_translations?.find((ct:any)=>ct?.locale?.toLowerCase()==='en')?.name ?? pref?.vehicle_type?.category_translations?.[0]?.name ?? '—'
  const gear     = pref?.gear ?? '—'
  const plateNo  = pref?.traffic_file_number ?? details?.vehicle_number ?? '—'
  const vendor   = details?.vendor ?? {}
  const school   = vendor?.vendor_translations?.find((vt:any)=>vt?.locale?.toLowerCase()==='en')?.name ?? vendor?.vendor_translations?.[0]?.name ?? details?.school_name ?? '—'
  const city     = pref?.city?.city_translations?.find((ct:any)=>ct?.locale?.toLowerCase()==='en')?.name ?? pref?.city?.city_translations?.[0]?.name ?? '—'
  const area     = pref?.state_province?.translations?.find((tr:any)=>tr?.locale?.toLowerCase()==='en')?.name ?? pref?.state_province?.translations?.[0]?.name ?? '—'

  // Add shift state
  const [showAddShift, setShowAddShift] = useState(false)
  const [shiftStart, setShiftStart]     = useState('')
  const [shiftEnd, setShiftEnd]         = useState('')

  // ── Working hours create modal state ──
  const [whModal, setWhModal]       = useState(false)
  const [whFullDay, setWhFullDay]   = useState(false)
  const [whSelected, setWhSelected] = useState<{start:string;end:string}[]>([])
  const [whSaving, setWhSaving]     = useState(false)

  // 90-minute slots generated from the trainer's shift range (mirrors the reference form)
  const shiftSlots: {start:string;end:string}[] = []
  shifts.forEach((s:any)=>{
    const [sh,sm] = String(s.shift_start_time??'').split(':').map(Number)
    const [eh,em] = String(s.shift_end_time??'').split(':').map(Number)
    if ([sh,sm,eh,em].some(n=>Number.isNaN(n))) return
    const fmt = (m:number)=>`${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`
    let cur = sh*60+sm
    const end = eh*60+em
    while (cur+90<=end) { shiftSlots.push({ start: fmt(cur), end: fmt(cur+90) }); cur+=90 }
  })

  const openWhModal = () => { setWhSelected([]); setWhFullDay(false); setWhModal(true) }
  const toggleSlot = (slot:{start:string;end:string}) =>
    setWhSelected(prev => prev.some(x=>x.start===slot.start&&x.end===slot.end)
      ? prev.filter(x=>!(x.start===slot.start&&x.end===slot.end))
      : [...prev, slot])

  const submitWorkingHours = async () => {
    const working_hours = whFullDay
      ? [{ start_time:'00:00', end_time:'23:59' }]
      : whSelected.map(s=>({ start_time:s.start, end_time:s.end }))
    if (!whFullDay && working_hours.length===0) { toast.error(t('userDetails.selectAtLeastOneSlot')); return }
    // create-update overwrites the trainer's existing hours, so confirm first.
    if (!(await confirmAction({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    setWhSaving(true)
    try {
      await restClient.post(endpoints.trainer.workingHours.createUpdate, {
        trainer_id: Number(id),
        day_of_week: 'EVERYDAY',
        working_hours,
        is_off_day: 0,
        is_full_day: whFullDay ? 1 : 0,
      })
      toast.success(t('userDetails.workingHoursSaved'))
      setWhModal(false)
      pkgQc.invalidateQueries({ queryKey: ['working-hours','trainer',id] })
    } catch (e:any) {
      const errs = e?.response?.data?.errors
      toast.error(errs
        ? (typeof errs==='string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? t('userDetails.failedToSaveWorkingHours')))
    } finally { setWhSaving(false) }
  }

  if (isLoading) return <div className="flex justify-center items-center h-64"><Spinner className="w-8 h-8"/></div>

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span className="cursor-pointer hover:text-gray-700" onClick={()=>navigate(listPath)}>{isSchool ? t('userDetails.breadcrumbTrainers') : t('userDetails.breadcrumbUsers')}</span>
            <ChevronRight size={11} className="rtl:rotate-180"/>
            <span className="cursor-pointer hover:text-gray-700" onClick={()=>navigate(listPath)}>{isTrainer ? t('userDetails.breadcrumbTrainers') : t('userDetails.breadcrumbUsers')}</span>
            <ChevronRight size={11} className="rtl:rotate-180"/>
            <span className="text-brand-500 font-semibold">{typeTitle}</span>
          </div>
          <h1 className="text-lg font-bold text-gray-900">{typeTitle}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 bg-white">
            <div className={cn('w-2 h-2 rounded-full',details?.is_active?'bg-green-500':'bg-red-400')}/>
            {t('userDetails.activeInactiveLabel')}
          </div>
          {!isSchoolAdmin && (
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold">
              <Trash2 size={12}/> {t('common.delete')}
            </button>
          )}
          {!isSchoolAdmin && (
            <button onClick={()=>navigate(isSchool ? `/school/trainer/${id}/edit` : `/dashboard/user/${id}/edit`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50">
              <Edit size={12}/> {t('userDetails.editProfileBtn')}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-gray-200">
        {TABS.map(tb=>(
          <button key={tb.value} onClick={()=>setTab(tb.value)}
            className={cn('px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors',
              tab===tb.value?'border-brand-500 text-brand-600 bg-brand-50':'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50')}>
            {tb.label}
          </button>
        ))}
      </div>

      {isTrainer && details?.certificate_expiry_date && tab==='details' && <CertBanner date={details.certificate_expiry_date}/>}

      {/* ════════ DETAILS TAB ════════ */}
      {tab==='details' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title={infoTitle} color="blue" icon={<User size={14}/>}>
            <div className="flex items-start gap-4 mb-5">
              <img src={details?.photo_url || DUMMY_AVATAR} alt={detailsName} onError={onAvatarError}
                className="w-28 h-28 rounded-xl object-cover border border-gray-100 flex-shrink-0 bg-gray-100"/>
              <div className="min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-gray-900 text-sm">{detailsName}</span>
                  <StatusPill active={!!details?.is_active}/>
                </div>
                <div className="text-xs text-gray-400">{idLabel}: #{isTrainer?'TR':''}{String(details?.id??'').padStart(4,'0')}</div>
                {isTrainer && avgRating!=null && <Stars n={avgRating}/>}
              </div>
            </div>
            <InfoRow label={t('userDetails.nameProfileCard')}       value={details?.name}/>
            {details?.name_ar && <InfoRow label={t('userDetails.nameProfileCardAr')}  value={details?.name_ar}/>}
            {details?.name_ur && <InfoRow label={t('userDetails.nameProfileCardUr')}  value={details?.name_ur}/>}
            {isTrainer && <InfoRow label={t('userDetails.certificateExpiryDate')} value={details?.certificate_expiry_date}/>}
            {details?.dob && <InfoRow label={t('bookingDetails.dateOfBirth')}   value={details?.dob?.split('T')[0]}/>}
            {details?.gender && <InfoRow label={t('bookingDetails.gender')}          value={details?.gender}/>}
            <InfoRow label={t('bookingDetails.email')}           value={details?.email}/>
            <InfoRow label={t('bookingDetails.phoneNumber')}    value={details?.phone?`${details?.country_code??''}${details?.country_code?'-':''}${details?.phone}`:'—'}/>
            <InfoRow label={t('userDetails.joinedDate')}     value={details?.created_at?`${new Date(details.created_at).toLocaleDateString('en-GB').replace(/\//g,'-')} ${new Date(details.created_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',hour12:true})}`:'—'}/>
            {!!details?.wallet_balance && (
              <InfoRow label={t('userDetails.walletBalance')} value={<DirhamAmount value={details.wallet_balance} size={12}/>}/>
            )}
          </SectionCard>

          {/* Students: every preference the user set in the app (trainers get theirs further down) */}
          {!isTrainer && (() => {
            const notSet = t('userDetails.prefNotSet')
            const text = (v:any) => {
              const s = typeof v === 'string' ? v : (v?.name ?? v?.language_name ?? v?.label ?? '')
              const out = String(s ?? '').trim()
              return out && out.toLowerCase() !== 'unknown' && out.toLowerCase() !== 'not specified' ? out : ''
            }
            const appLang = details?.app_language
            const rows: { sub:string; icon:React.ReactNode; val:React.ReactNode }[] = [
              { sub:t('userDetails.prefCity'), icon:<MapPin size={13} className="text-gray-400"/>, val: city!=='—' ? city : notSet },
              { sub:t('userDetails.prefArea'), icon:<MapPin size={13} className="text-gray-400"/>, val: area!=='—' ? area : notSet },
              { sub:t('userDetails.prefGear'), icon:<span className="text-gray-400 text-xs">⚙</span>, val: text(gear) || notSet },
              { sub:t('userDetails.prefGender'), icon:<User size={13} className="text-gray-400"/>, val: text(pref?.gender ?? details?.gender) || notSet },
              { sub:t('userDetails.prefAppLanguage'), icon:<span className="text-gray-400">🌐</span>,
                val: appLang?.name ? (
                  <span className="inline-flex items-center gap-1.5">
                    {appLang?.flag?.virtual_path && <img src={appLang.flag.virtual_path} alt="" className="w-4 h-3 object-cover rounded-[2px]"/>}
                    {appLang.name}
                  </span>
                ) : notSet },
            ]
            return (
              <SectionCard title={t('userDetails.userPreferencesTitle')} color="blue" icon={<Car size={14}/>}>
                <div className="grid grid-cols-2 gap-x-5 gap-y-0">
                  {rows.map((row,i)=>(
                    <div key={i} className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
                      <span className="w-4 flex items-center justify-center flex-shrink-0">{row.icon}</span>
                      <div className="min-w-0">
                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wide leading-tight">{row.sub}</div>
                        <div className="text-xs font-semibold text-gray-800 truncate leading-snug">{row.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {!!pref?.bio && <p className="text-xs text-gray-700 leading-relaxed mt-3 pt-3 border-t border-gray-100">{pref.bio}</p>}
              </SectionCard>
            )
          })()}

          {isTrainer && (<>
          <SectionCard title={t('userDetails.revenueTitle')} color="amber" icon={<DollarSign size={14}/>}>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-4 bg-brand-50 rounded-xl">
                <DirhamAmount value={details?.total_revenue??0} size={16} className="justify-center" textClass="text-2xl font-black text-gray-900"/>
                <div className="text-xs text-gray-500 mt-2 font-medium">{t('userDetails.totalRevenue')}</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-xl">
                <DirhamAmount value={details?.trainer_earning??details?.trainer_revenue??0} size={16} className="justify-center" textClass="text-2xl font-black text-gray-900"/>
                <div className="text-xs text-gray-500 mt-2 font-medium">{t('userDetails.trainerRevenue')}</div>
              </div>
            </div>
            <button className="w-full py-1.5 rounded-lg border border-brand-300 text-brand-600 text-xs font-semibold hover:bg-brand-50">{t('userDetails.viewAllRevenue')}</button>
          </SectionCard>

          <SectionCard title={t('userDetails.trainerRewardTitle')} color="blue" icon={<Star size={14}/>}>
            <div className="text-center py-6">
              <DirhamAmount value={details?.trainer_reward??0} size={20} className="justify-center" textClass="text-4xl font-black text-gray-900"/>
              <div className="text-sm text-gray-500 mt-2 font-medium">{t('userDetails.totalReward')}</div>
            </div>
            <button className="w-full py-1.5 rounded-lg border border-brand-300 text-brand-600 text-xs font-semibold hover:bg-brand-50">{t('userDetails.viewAllRevenue')}</button>
          </SectionCard>

          <SectionCard title={t('userDetails.aboutTitle')} color="brown" icon={<FileText size={14}/>}>
            <p className="text-xs text-gray-700 leading-relaxed">{pref?.bio||'—'}</p>
            {pref?.bio_ar&&<p className="text-xs text-gray-600 leading-relaxed mt-3 text-end" dir="rtl">{pref.bio_ar}</p>}
            {pref?.bio_ur&&<p className="text-xs text-gray-600 leading-relaxed mt-2 text-end" dir="rtl">{pref.bio_ur}</p>}
          </SectionCard>

          <SectionCard title={t('userDetails.userPreferencesTitle')} color="blue" icon={<Car size={14}/>}>
            <div className="grid grid-cols-2 gap-x-5 gap-y-0">
              {[
                {sub:t('userDetails.prefVehicle'),icon:<Car size={13} className="text-gray-400"/>,val:vehicle},
                {sub:t('userDetails.prefGear'),icon:<span className="text-gray-400 text-xs">⚙</span>,val:gear},
                {sub:t('userDetails.prefPlateNumber'),icon:<span className="text-gray-400 text-[11px] font-mono font-bold">123</span>,val:plateNo},
                {sub:t('userDetails.prefSchool'),icon:<Building2 size={13} className="text-gray-400"/>,val:school},
                {sub:t('userDetails.prefLanguages'),icon:<span className="text-gray-400">🌐</span>,val:(details?.languages??[]).map((l:any)=>l.dialect?.language_name??l.name??'').filter(Boolean).join(', ')||'—'},
                {sub:t('userDetails.prefRating'),icon:<span className="text-brand-400 text-sm">★</span>,val:avgRating!=null?String(avgRating):'—'},
                {sub:t('userDetails.prefCity'),icon:<MapPin size={13} className="text-gray-400"/>,val:city},
                {sub:t('userDetails.prefArea'),icon:<MapPin size={13} className="text-gray-400"/>,val:area},
                {sub:t('userDetails.prefMaxRadius'),icon:<span className="text-gray-400 text-[12px]">📏</span>,val:pref?.max_radius_in_km?t('userDetails.kmSuffix',{n:pref.max_radius_in_km}):'—'},
                {sub:t('userDetails.prefPickupOption'),icon:<span className="text-gray-400 text-[12px]">📍</span>,val:pref?.is_pickup_enabled?t('common.yes'):t('common.no')},
              ].map((row,i)=>(
                <div key={i} className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
                  <span className="w-4 flex items-center justify-center flex-shrink-0">{row.icon}</span>
                  <div className="min-w-0">
                    <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wide leading-tight">{row.sub}</div>
                    <div className="text-xs font-semibold text-gray-800 truncate leading-snug">{row.val}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
          </>)}

          <SectionCard title={t('userDetails.addressTitle')} color="green" icon={<MapPin size={14}/>}
            action={<button onClick={()=>setAddressModal({open:true,address:null})} className="text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-0.5 rounded font-semibold flex items-center gap-1"><Plus size={10}/> {t('userDetails.addNewAddress')}</button>}>
            {addresses.length>0?(() => {
              const shown=showAllAddresses?addresses:addresses.slice(0,1)
              return (<>
              {shown.map((addr:any,idx:number)=>{
              const addrArea=addr?.state_province?.translations?.find((tr:any)=>tr?.locale?.toLowerCase()==='en')?.name??addr?.state_province?.translations?.[0]?.name
              const addrCity=addr?.city_id_city?.city_translations?.find((ct:any)=>ct?.locale?.toLowerCase()==='en')?.name??addr?.city_id_city?.city_translations?.[0]?.name??addr?.city
              return (<div key={addr?.id??idx} className={idx>0?'mt-4 pt-4 border-t border-gray-100':''}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    {!!addr?.label && <span className="text-xs font-bold text-gray-700">{addr.label}</span>}
                    {addr?.is_default
                      ? <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-bold">{t('userDetails.defaultBadge')}</span>
                      : <button onClick={()=>handleSetDefaultAddress(addr)} disabled={settingDefaultId===addr?.id}
                          className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-bold hover:bg-gray-200 disabled:opacity-50">
                          {settingDefaultId===addr?.id?t('userDetails.settingDefault'):t('userDetails.setDefault')}
                        </button>}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={()=>setAddressModal({open:true,address:addr})} className="text-xs text-green-600 font-semibold flex items-center gap-1 hover:underline"><Edit size={11}/> {t('common.edit')}</button>
                    <button onClick={()=>handleDeleteAddress(addr)} disabled={deletingAddressId===addr?.id}
                      className="text-xs text-red-500 font-semibold flex items-center gap-1 hover:underline disabled:opacity-50"><Trash2 size={11}/> {t('common.delete')}</button>
                  </div>
                </div>
                <InfoRow label={t('userDetails.areaLabel')}                value={addrArea}/>
                <InfoRow label={t('userDetails.streetLabel')}              value={addr?.street??addr?.address}/>
                {!!addr?.building_name && <InfoRow label={t('userDetails.buildingVillaNo')} value={addr.building_name}/>}
                {!!addr?.landmark && <InfoRow label={t('userDetails.landMark')} value={addr.landmark}/>}
                <InfoRow label={t('bookingDetails.phoneNumber')}        value={addr?.phone_number}/>
                {!!addr?.plot_number && <InfoRow label={t('userDetails.plotNumber')} value={addr.plot_number}/>}
                <InfoRow label={t('bookingDetails.city')}                value={addrCity}/>
                {!!addr?.neighbourhood && <InfoRow label={t('userDetails.neighbourhood')} value={addr.neighbourhood}/>}
                {/* Location on map */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[12px] text-gray-400 font-bold uppercase tracking-wider">{t('userDetails.locationLabel')}</div>
                    {addr?.latitude != null && addr?.longitude != null && (
                      <button onClick={()=>handleCopyLocation(addr)}
                        className="flex items-center gap-1 text-[11px] font-semibold text-green-600 hover:underline">
                        {copiedAddrId===addr?.id ? <Check size={11}/> : <Copy size={11}/>}
                        {copiedAddrId===addr?.id ? t('userDetails.copied') : t('userDetails.copyLocation')}
                      </button>
                    )}
                  </div>
                  <LocationMap lat={addr?.latitude} lng={addr?.longitude} title={addr?.label ?? detailsName}/>
                </div>
              </div>)
              })}
                {addresses.length>1&&<button onClick={()=>setShowAllAddresses(v=>!v)} className="mt-3 w-full py-1.5 rounded-lg border border-green-300 text-green-600 text-xs font-semibold hover:bg-green-50">{showAllAddresses?t('common.showLess'):`${t('userDetails.viewAllAddress')} (${addresses.length})`}</button>}
              </>)
            })():<p className="text-xs text-gray-400 text-center py-4">{t('userDetails.noAddressesAdded')}</p>}
          </SectionCard>

          <SectionCard title={t('userDetails.accountStatusTitle')} color="blue" icon={<Shield size={14}/>}>
            <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
              <span className="text-xs font-semibold text-gray-600">{t('userDetails.accountStatusLabel')}</span>
              <span className={cn('px-3 py-0.5 rounded-full text-xs font-bold',details?.is_active?'bg-green-100 text-green-700':'bg-red-100 text-red-600')}>{details?.is_active?t('common.yes'):t('common.no')}</span>
            </div>
            {isTrainer&&!isSchoolAdmin&&<>
              <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
                <span className="text-xs font-semibold text-gray-600">{t('userDetails.adminSuspended')}</span>
                <Toggle on={!!details?.is_suspended} onChange={handleSuspend} disabled={suspending}/>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
                <span className="text-xs font-semibold text-gray-600">{t('userDetails.autoSuspended')}</span>
                <span className="px-3 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-500">
                  {details?.max_cash_in_hand_allowed&&details?.cash_in_hand>=details?.max_cash_in_hand_allowed?t('common.yes'):t('common.no')}
                </span>
              </div>
              <div className="flex items-start justify-between py-2.5">
                <span className="text-xs font-semibold text-gray-600 pt-0.5">{t('userDetails.verificationLabel')}</span>
                {(details?.is_verified || details?.verified_at)
                  ? <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-300">
                        <Check size={11}/>{details?.verified_at ? t('userDetails.verifiedOn',{date:new Date(details.verified_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}) : t('userDetails.verified')}
                      </span>
                      <button onClick={() => handleVerify(false)} disabled={verifying} className="px-3 py-1 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 disabled:opacity-50 flex items-center gap-1"><X size={11}/> {verifying ? t('userDetails.updating') : t('userDetails.unverify')}</button>
                    </div>
                  : <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-300"><X size={11}/> {t('userDetails.notVerified')}</span>
                      <button onClick={() => handleVerify(true)} disabled={verifying} className="px-3 py-1 rounded-lg bg-brand-500 text-white text-xs font-semibold hover:bg-brand-600 disabled:opacity-50 flex items-center gap-1"><Check size={11}/> {verifying ? t('userDetails.verifying') : t('userDetails.verify')}</button>
                    </div>
                }
              </div>
            </>}
          </SectionCard>

          {isTrainer&&(
            <SectionCard title={t('userDetails.schoolFinancialSummary')} color="blue" icon={<DollarSign size={14}/>}>
              <InfoRow label={t('userDetails.maxCashAllowed')} value={<DirhamAmount value={details?.max_cash_in_hand_allowed}/>}/>
              <InfoRow label={t('userDetails.cashInHand')}             value={<DirhamAmount value={details?.cash_in_hand}/>}/>
              <InfoRow label={t('userDetails.cashClearanceDate')}      value={details?.cash_clearance_date?new Date(details.cash_clearance_date).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'2-digit',minute:'2-digit'}):'—'}/>
              <InfoRow label={t('userDetails.lastBookingAt')}          value={details?.last_booking_was?new Date(details.last_booking_was).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'2-digit',minute:'2-digit'}):'—'}/>
            </SectionCard>
          )}

          {isTrainer&&(
            <SectionCard title={t('userDetails.trainingLicense')} color="purple" icon={<FileText size={14}/>}>
              <div className="grid grid-cols-2 gap-3">
                {['FRONT','BACK'].map((side,i)=>{
                  // Match an existing doc for this side; fall back to positional docs[i] for
                  // legacy rows saved without doc_side, so old records still show up correctly.
                  const sideText = side==='FRONT' ? t('userDetails.sideFront') : t('userDetails.sideBack')
                  const doc = docs.find((d:any)=>(d.doc_side??'').toUpperCase()===side) ?? (!docs[i]?.doc_side ? docs[i] : undefined)
                  const url = doc ? (doc.doc_file ?? doc.document_url) : null
                  const label = doc ? [doc.doc_type, sideText].filter(Boolean).join(' · ') : ''

                  if (!doc) return (
                    <button key={side} onClick={()=>setDocumentModal({open:true,doc:{doc_type:'Driving Licence',doc_side:side}})}
                      className="rounded-xl border border-dashed border-gray-300 h-36 flex flex-col items-center justify-center gap-1.5 text-gray-400 hover:border-brand-300 hover:text-brand-500 hover:bg-brand-50/30 transition-colors">
                      <Plus size={20}/>
                      <span className="text-xs font-semibold">{t('userDetails.addSide',{side:sideText})}</span>
                    </button>
                  )

                  return (
                    <div key={doc.id??side} className="rounded-xl overflow-hidden border border-brand-300">
                      <div className="flex items-center justify-between px-3 py-1.5 text-xs font-bold text-white bg-brand-400">
                        <span>{t('userDetails.sideLabel',{side:sideText})}</span>
                        <div className="flex items-center gap-2">
                          {url && <button onClick={()=>downloadDoc(url, doc.file_name ?? doc.doc_type ?? `licence-${side}`)} title={t('common.download')}><Download size={12}/></button>}
                          <button onClick={()=>setDocumentModal({open:true,doc})} title={t('common.edit')}><Edit size={12}/></button>
                        </div>
                      </div>
                      {url
                        ? <button onClick={()=>setDocPreview({...doc, url, label})} className="block w-full cursor-zoom-in"><img src={url} alt={label} className="w-full h-32 object-cover"/></button>
                        : <div className="h-32 bg-gray-50 flex flex-col items-center justify-center gap-1 p-3"><FileText size={22} className="text-gray-300"/><p className="text-xs text-gray-400">{t('userDetails.noFile')}</p></div>
                      }
                    </div>
                  )
                })}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* ════════ REVIEWS TAB ════════
          API: get-trainers-reviews?trainer_id=X
          data[0].reviews[], data[0].avg_rating, data[0].rating_breakdown, data[0].rating_count
          Each review: {session_id, user_rating, user_comments, student_name, student_id, uploaded_at, booking_id}
      ════════ */}
      {tab==='reviews' && (
        <div className="space-y-3">
          {/* Rating summary */}
          {reviewCount>0&&(
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-5xl font-black text-gray-900">{reviewAvg.toFixed(1)}</div>
                  <Stars n={reviewAvg}/>
                  <div className="text-xs text-gray-400 mt-1">{t('userDetails.ratingsCount',{count:reviewCount})}</div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {[5,4,3,2,1].map(star=>{
                    const count = reviewBreakdown[`${star}_star`]??0
                    const tot   = Object.values(reviewBreakdown as Record<string,number>).reduce((a:number,b:any)=>a+(b as number),0)
                    const pct   = tot>0?(count/tot)*100:0
                    return (
                      <div key={star} className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="w-4 text-right">{star}</span>
                        <span className="text-brand-400">★</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-400 rounded-full" style={{width:`${pct}%`}}/>
                        </div>
                        <span className="w-4">{count}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 2-column review grid matching screenshot */}
          {reviews.length===0
            ? <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">{t('userDetails.noReviewsYet')}</div>
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {reviews.map((r:any,i:number)=>(
                  <div key={r.session_id??i} className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Avatar */}
                        <img src={r.student_photo_url || r.photo_url || DUMMY_AVATAR} alt={r.student_name ?? ''} onError={onAvatarError}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-gray-100"/>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-gray-900 truncate">{r.student_name??'—'}</div>
                          <div className="text-xs text-gray-400">
                            {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString('en-GB').replace(/\//g,'-') : '—'}
                          </div>
                          <Stars n={r.user_rating??0}/>
                        </div>
                      </div>
                      {/* Flag + delete */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                          <span className="text-[12px]">🚩</span>
                        </button>
                        <button onClick={()=>setReviewDeleteTarget(r)} title={t('userDetails.deleteReviewTooltip')}
                          className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center hover:bg-red-100">
                          <Trash size={11} className="text-red-500"/>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mt-3 line-clamp-3">{r.user_comments||t('userDetails.noComment')}</p>
                  </div>
                ))}
              </div>
            )
          }
          {reviewTotal>reviewPerPage&&(
            <div className="bg-white rounded-xl border border-gray-100">
              <Pagination page={reviewPage} lastPage={reviewLastPage} total={reviewTotal} perPage={reviewPerPage}
                onPageChange={setReviewPage} onPerPageChange={n=>{setReviewPerPage(n);setReviewPage(1)}}/>
            </div>
          )}

          {/* Delete review confirmation */}
          <ConfirmDialog
            open={!!reviewDeleteTarget}
            onClose={()=>setReviewDeleteTarget(null)}
            onConfirm={confirmDeleteReview}
            title={t('userDetails.deleteReviewTitle')}
            description={t('userDetails.deleteReviewDescription',{from:reviewDeleteTarget?.student_name ? t('userDetails.deleteReviewFrom',{name:reviewDeleteTarget.student_name}) : ''})}
            confirmLabel={t('common.delete')}
            variant="danger"
            loading={reviewDeleting}
          />
        </div>
      )}

      {/* ════════ PACKAGES TAB ════════
          API: public/package/get-packge-list-with-global-package
          data[].price, data[].package.package_translations[locale=En].name
          data[].package.number_of_sessions, data[].package.background_color
          data[].package.session_details[].session_inclusions (HTML)
      ════════ */}
      {tab==='packages' && (
        <div className="space-y-4">

          {/* ── Toolbar: Add Package button (hidden for school admins — read-only) ── */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{t('userDetails.packagesAssignedCount',{count:packages.length})}</p>
            {!isSchoolAdmin && (
              <button onClick={openAddPkg}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-opacity"
                style={{background:'#f68b2c'}}>
                <Plus size={14}/> {t('userDetails.addPackage')}
              </button>
            )}
          </div>

          {/* ── Package cards grid ── */}
          {pkgLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[1,2,3,4].map(i=><div key={i} className="h-72 rounded-2xl bg-gray-100 animate-pulse"/>)}
            </div>
          ) : packages.length===0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <p className="text-gray-400 text-sm mb-3">{t('userDetails.noPackagesAssigned')}</p>
              {!isSchoolAdmin && (
                <button onClick={openAddPkg}
                  className="px-4 py-2 rounded-xl text-sm font-bold text-white hover:opacity-90"
                  style={{background:'#f68b2c'}}>{t('userDetails.addFirstPackage')}</button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {packages.map((item:any, i:number) => {
                // item shape: {id, package_id, price, status, is_published, package:{...}}
                const pkg        = item.package ?? item
                const trans      = pkg.package_translations?.find((pt:any)=>pt?.locale?.toLowerCase()==='en') ?? pkg.package_translations?.[0]
                const name       = trans?.name ?? `Package ${pkg.id}`
                const desc       = trans?.description ?? ''
                const minPrice   = Number(pkg.min_price ?? 0)
                const maxPrice   = Number(pkg.max_price ?? 0)
                const trainerPrice = Number(item.price ?? minPrice)
                const sessions   = pkg.is_unlimited ? null : (pkg.number_of_sessions ?? 0)
                const isActive   = item.status === 1
                const isPublished = item.is_published === 1
                const theme      = pkgTheme(pkg.background_color ?? '')

                // Feature bullets
                const rawHtml = trans?.session_inclusions ?? ''
                const htmlLines: string[] = rawHtml
                  ? rawHtml.replace(/<br\s*\/?>\s*/gi,'\n').replace(/<li[^>]*>/gi,'\n').replace(/<[^>]+>/g,'').split('\n').map((s:string)=>s.trim()).filter(Boolean)
                  : []
                const fallback: string[] = !htmlLines.length ? [
                  ...(pkg.is_unlimited ? [t('userDetails.pkgFeatureUnlimited')] : []),
                  ...(pkg.is_pickup_fee_included ? [t('userDetails.pkgFeatureFreePickup')] : []),
                  ...(pkg.is_certificate_included ? [t('userDetails.pkgFeatureCertificate')] : []),
                  ...(Number(pkg.free_km)>0 ? [t('userDetails.pkgFeatureFreeKm',{n:pkg.free_km})] : []),
                  ...(pkg.free_reschedule_count>0 ? [t('userDetails.pkgFeatureFreeReschedule',{count:pkg.free_reschedule_count})] : []),
                  ...(pkg.free_trainer_change_count>0 ? [t('userDetails.pkgFeatureTrainerChange',{count:pkg.free_trainer_change_count})] : []),
                ] : []
                const features = htmlLines.length ? htmlLines : fallback

                return (
                  <div key={item.id ?? i}
                    className="rounded-2xl border overflow-hidden shadow-sm flex flex-col transition-transform hover:scale-[1.015]"
                    style={{background:theme.bg, borderColor:theme.border}}>

                    <div className="p-5 flex-1">
                      {/* Label */}
                      <p className="text-[12px] font-bold uppercase tracking-widest mb-1" style={{color:theme.check}}>
                        {pkg.background_color ? `${pkg.background_color.charAt(0).toUpperCase()+pkg.background_color.slice(1)} ${t('userDetails.packageWord')}` : t('userDetails.packageWord')}
                      </p>
                      <h3 className="text-base font-bold leading-snug mb-1" style={{color:theme.text}}>{name}</h3>
                      {desc && <p className="text-xs opacity-60 leading-snug mb-3" style={{color:theme.text}}>{desc}</p>}

                      {/* Price */}
                      <div className="mb-4">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-3xl font-black" style={{color:theme.text}}>{trainerPrice.toLocaleString()}</span>
                          <span className="text-sm font-bold" style={{color:theme.text}}>AED</span>
                          {minPrice !== maxPrice && (
                            <span className="text-xs opacity-40 ml-1" style={{color:theme.text}}>({minPrice}–{maxPrice})</span>
                          )}
                        </div>
                        <p className="text-xs opacity-50 mt-0.5" style={{color:theme.text}}>
                          {pkg.is_unlimited ? t('userDetails.unlimitedSessions') : sessions ? t('userDetails.sessionsCount',{n:sessions}) : ''}
                        </p>
                      </div>

                      {/* Features */}
                      <div className="space-y-2">
                        {features.map((f:string,j:number)=>(
                          <div key={j} className="flex items-start gap-2">
                            <Check size={13} className="mt-0.5 flex-shrink-0" style={{color:theme.check}}/>
                            <span className="text-xs leading-snug" style={{color:theme.text}}>{f}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer: status toggles + actions (hidden for school admins — read-only) */}
                    {!isSchoolAdmin && (
                    <div className="px-4 py-3 space-y-2" style={{borderTop:`1px solid ${theme.border}`}}>
                      {/* Status row */}
                      <div className="flex items-center justify-between text-xs" style={{color:theme.text}}>
                        <span className="font-semibold opacity-70">{t('common.status')}</span>
                        <button
                          onClick={()=>openEditPkg({...item, status: isActive?0:1})}
                          className="relative w-10 h-5 rounded-full transition-colors"
                          style={{background: isActive ? '#f68b2c' : '#d1d5db'}}
                          title={isActive?t('common.active'):t('common.inactive')}>
                          <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all', isActive?'left-5':'left-0.5')}/>
                        </button>
                      </div>
                      {/* Published row */}
                      <div className="flex items-center justify-between text-xs" style={{color:theme.text}}>
                        <span className="font-semibold opacity-70">{t('userDetails.published')}</span>
                        <button
                          onClick={()=>openEditPkg({...item, is_published: isPublished?0:1})}
                          className="relative w-10 h-5 rounded-full transition-colors"
                          style={{background: isPublished ? '#22c55e' : '#d1d5db'}}
                          title={isPublished?t('userDetails.published'):t('userDetails.unpublished')}>
                          <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all', isPublished?'left-5':'left-0.5')}/>
                        </button>
                      </div>
                      {/* Edit + Delete */}
                      <div className="flex items-center justify-between pt-1">
                        <button onClick={()=>openEditPkg(item)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border hover:opacity-80 transition-opacity"
                          style={{borderColor:theme.border, color:theme.text}}>
                          <Edit size={11}/> {t('common.edit')}
                        </button>
                        <button
                          onClick={()=>deletePkg(item.id)}
                          disabled={pkgDeleting===item.id}
                          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/10 transition-colors disabled:opacity-40">
                          <Trash size={13} style={{color:theme.text, opacity:0.6}}/>
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* ══════ ADD / EDIT PACKAGE MODAL ══════ */}
          {pkgModal.open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.4)'}}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Modal header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <h3 className="text-base font-bold text-gray-900">
                    {pkgModal.mode==='add' ? t('userDetails.addPackageTitle') : t('userDetails.editPackageTitle')}
                  </h3>
                  <button onClick={()=>setPkgModal({open:false,mode:'add',pkg:null})}
                    className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                    <X size={16} className="text-gray-500"/>
                  </button>
                </div>

                {/* Modal body */}
                <div className="px-6 py-5 space-y-4">
                  {pkgErrors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm text-red-600">{pkgErrors.submit}</div>
                  )}

                  {/* Package selector — only in Add mode */}
                  {pkgModal.mode==='add' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">{t('userDetails.packageFieldLabel')} <span className="text-red-500">*</span></label>
                      <select
                        value={pkgForm.package_id}
                        disabled={availPkgLoading}
                        onChange={e=>{ setPkgForm(p=>({...p,package_id:e.target.value})); setPkgErrors(p=>({...p,package_id:''})) }}
                        className={cn('w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white disabled:bg-gray-50 disabled:text-gray-400',
                          pkgErrors.package_id ? 'border-red-400' : 'border-gray-200')}>
                        <option value="">
                          {availPkgLoading ? t('userDetails.loadingPackages') : t('userDetails.selectPackage')}
                        </option>
                        {availPackages.map((p:any)=>{
                          const tr = p.package_translations?.find((pt:any)=>pt?.locale?.toLowerCase()==='en') ?? p.package_translations?.[0]
                          return <option key={p.id} value={p.id}>{tr?.name ?? t('userDetails.packageHash',{id:p.id})} ({p.min_price}–{p.max_price} AED)</option>
                        })}
                      </select>
                      {availPkgLoading && (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-400">
                          <Spinner className="w-3 h-3"/> {t('userDetails.loadingPackages')}
                        </p>
                      )}
                      {!availPkgLoading && availPackages.length===0 && (
                        <p className="mt-1 text-xs text-gray-400">{t('userDetails.noPackagesAvailable')}</p>
                      )}
                      {pkgErrors.package_id && <p className="mt-1 text-xs text-red-500">{pkgErrors.package_id}</p>}
                    </div>
                  )}

                  {/* Trainer Price */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('userDetails.trainerPriceLabel')} <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      value={pkgForm.trainer_price}
                      onChange={e=>{ setPkgForm(p=>({...p,trainer_price:e.target.value})); setPkgErrors(p=>({...p,trainer_price:''})) }}
                      placeholder={t('userDetails.enterPrice')}
                      className={cn('w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400',
                        pkgErrors.trainer_price ? 'border-red-400 bg-red-50' : 'border-gray-200')}
                    />
                    {pkgErrors.trainer_price && <p className="mt-1 text-xs text-red-500">{pkgErrors.trainer_price}</p>}
                  </div>

                  {/* Status + Published toggles */}
                  <div className="grid grid-cols-2 gap-4 pt-1">
                    <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                      <span className="text-sm font-semibold text-gray-700">{t('common.status')}</span>
                      <button
                        type="button"
                        onClick={()=>setPkgForm(p=>({...p,status:!p.status}))}
                        className="relative w-11 h-6 rounded-full transition-colors"
                        style={{background: pkgForm.status ? '#f68b2c' : '#d1d5db'}}>
                        <span className={cn('absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all', pkgForm.status?'left-5':'left-0.5')}/>
                      </button>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                      <span className="text-sm font-semibold text-gray-700">{t('userDetails.published')}</span>
                      <button
                        type="button"
                        onClick={()=>setPkgForm(p=>({...p,is_published:!p.is_published}))}
                        className="relative w-11 h-6 rounded-full transition-colors"
                        style={{background: pkgForm.is_published ? '#22c55e' : '#d1d5db'}}>
                        <span className={cn('absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all', pkgForm.is_published?'left-5':'left-0.5')}/>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Modal footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <button onClick={()=>setPkgModal({open:false,mode:'add',pkg:null})}
                    className="px-5 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-600 hover:bg-white transition-colors">
                    {t('common.cancel')}
                  </button>
                  <button onClick={submitPkg} disabled={pkgSaving}
                    className="px-6 py-2 text-sm font-bold rounded-xl text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                    style={{background:'#f68b2c'}}>
                    {pkgSaving ? t('userDetails.saving') : pkgModal.mode==='add' ? t('userDetails.addPackage') : t('userDetails.saveChanges')}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

            {tab==='working-hours' && (
        <div className="space-y-4">
          {/* Shifts section */}
          <div className="bg-white rounded-xl border border-gray-100 p-5" style={{maxWidth:400}}>
            {shifts.map((s:any,i:number)=>(
              <div key={s.id??i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                <div>
                  <div className="text-[11px] text-[#f68b2c] font-bold uppercase tracking-wide mb-0.5">{t('userDetails.shiftLabel',{n:String(i+1).padStart(3,'0')})}</div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Clock size={14} className="text-gray-400"/>
                    {fmt12(s.shift_start_time)} - {fmt12(s.shift_end_time)}
                  </div>
                </div>
                {!isSchoolAdmin && (
                  <button className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center hover:bg-red-100">
                    <Trash size={13} className="text-red-500"/>
                  </button>
                )}
              </div>
            ))}

            {/* Add shift inline form — admins only */}
            {!isSchoolAdmin && (showAddShift?(
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('userDetails.startTime')} *</label>
                    <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1.5 gap-2">
                      <input type="time" value={shiftStart} onChange={e=>setShiftStart(e.target.value)}
                        className="text-sm focus:outline-none flex-1"/>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('userDetails.endTime')} *</label>
                    <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1.5 gap-2">
                      <input type="time" value={shiftEnd} onChange={e=>setShiftEnd(e.target.value)}
                        className="text-sm focus:outline-none flex-1"/>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={()=>setShowAddShift(false)}
                    className="flex-1 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50">{t('common.cancel')}</button>
                  <button onClick={()=>setShowAddShift(false)}
                    className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white hover:opacity-90" style={{background:'#f68b2c'}}>{t('common.save')}</button>
                </div>
              </div>
            ):(
              <button onClick={()=>setShowAddShift(true)}
                className="mt-3 w-full py-2 rounded-lg border text-xs font-semibold hover:bg-gray-50" style={{borderColor:'#f68b2c',color:'#f68b2c'}}>
                + {t('userDetails.addShift')}
              </button>
            ))}
          </div>

          {/* Working hours table — only shown when the trainer has a shift, mirrors the API working-hours list */}
          {shifts.length>0&&(
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 font-bold text-gray-900">
                  <Clock size={18} className="text-gray-500"/> {t('userDetails.trainerWorkingHours')}
                </div>
                {!isSchoolAdmin && (
                  <button onClick={openWhModal}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white hover:opacity-90" style={{background:'#f68b2c'}}>
                    + {t('userDetails.addWorkingHours')}
                  </button>
                )}
              </div>
              {Object.keys(whByDay).length===0?(
                <p className="text-sm py-4" style={{color:'#f68b2c'}}>{t('userDetails.noWorkingHours')}</p>
              ):(
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-start">
                        <th className="px-4 py-2.5 font-semibold text-gray-600 rounded-s-lg">{t('userDetails.dayCol')}</th>
                        <th className="px-4 py-2.5 font-semibold text-gray-600">{t('userDetails.offDayCol')}</th>
                        <th className="px-4 py-2.5 font-semibold text-gray-600 rounded-e-lg">{t('userDetails.availableTimeCol')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DAYS.filter(d=>whByDay[d]).map(day=>{
                        const hours   = whByDay[day]
                        const isOff   = hours[0]?.is_off_day
                        const isFull  = hours[0]?.is_full_day
                        return (
                          <tr key={day} className="border-b border-gray-50 last:border-0">
                            <td className="px-4 py-3 text-gray-700">{t(`userDetails.dayNames.${day}`)}</td>
                            <td className="px-4 py-3 text-gray-600">{isOff?t('common.yes'):t('common.no')}</td>
                            <td className="px-4 py-3">
                              {isOff?(
                                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-50 text-brand-600">{t('userDetails.offDayBadge')}</span>
                              ):isFull?(
                                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{background:'#f68b2c'}}>{t('userDetails.fullDayBadge')}</span>
                              ):(
                                <div className="space-y-0.5">
                                  {hours
                                    .slice()
                                    .sort((a:any,b:any)=>(a.start_time??'').localeCompare(b.start_time??''))
                                    .map((h:any,hi:number)=>(
                                      <div key={hi} className="text-gray-700">{fmt12(h.start_time)} - {fmt12(h.end_time)}</div>
                                    ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Add Working Hours modal ── */}
          {whModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.4)'}}>
              <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h3 className="font-bold text-gray-900">{t('userDetails.addWorkingHours')}</h3>
                  <button onClick={()=>setWhModal(false)}><X size={18} className="text-gray-400 hover:text-gray-600"/></button>
                </div>

                <div className="p-5 space-y-4">
                  <p className="text-sm font-semibold text-gray-700">{t('userDetails.selectShiftsPrompt')}</p>

                  {/* Full day toggle */}
                  <label className="flex items-center gap-2 cursor-pointer w-fit">
                    <input type="checkbox" checked={whFullDay} onChange={e=>setWhFullDay(e.target.checked)}
                      className="w-4 h-4 rounded accent-[#f68b2c]"/>
                    <span className="text-sm font-medium text-gray-700">{t('userDetails.isFullDay')}</span>
                  </label>

                  {whFullDay ? (
                    <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                      {t('userDetails.fullDayNote')}
                    </p>
                  ) : shiftSlots.length>0 ? (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">{t('userDetails.tapSlotsPrompt')}</p>
                      <div className="flex flex-wrap gap-2">
                        {shiftSlots.map((slot,i)=>{
                          const sel = whSelected.some(x=>x.start===slot.start&&x.end===slot.end)
                          return (
                            <button key={i} onClick={()=>toggleSlot(slot)}
                              className={cn('px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                                sel ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 hover:bg-gray-50')}
                              style={sel?{background:'#f68b2c'}:{}}>
                              {fmt12(slot.start+':00')} - {fmt12(slot.end+':00')}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">{t('userDetails.noShiftConfigured')}</p>
                  )}
                </div>

                <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
                  <button onClick={()=>setWhModal(false)}
                    className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">{t('common.cancel')}</button>
                  <button onClick={submitWorkingHours} disabled={whSaving}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{background:'#f68b2c'}}>
                    {whSaving ? t('userDetails.saving') : t('userDetails.create')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════ LEAVE DAYS TAB — GET admin/leave/get-leave-list?trainer_id=&page=&limit= ════════ */}
      {tab==='leave-days' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between gap-3" style={{background:'#f68b2c'}}>
            <div>
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <Calendar size={15}/> {t('userDetails.leaveDaysTitle')}
                {leaveTotal > 0 && (
                  <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-white/20 text-white tabular-nums">
                    {leaveTotal}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-white/80 mt-0.5">{t('userDetails.leaveDaysSubtitle')}</p>
            </div>
            {!isSchoolAdmin && (
              <button onClick={()=>openAddLeave()}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0">
                <Plus size={13}/> {t('userDetails.addLeave')}
              </button>
            )}
          </div>

          <div className="p-5">
            {leaveLoading ? (
              <div className="flex justify-center py-10"><Spinner/></div>
            ) : leaveDays.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{background:'#fef1e5'}}>
                  <CalendarOff size={24} style={{color:'#f68b2c'}}/>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{t('userDetails.noLeaveDays')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t('userDetails.noLeaveDaysHint')}</p>
                </div>
                {!isSchoolAdmin && (
                  <button onClick={()=>openAddLeave()}
                    className="flex items-center gap-1.5 mt-1 px-4 py-2 rounded-lg text-xs font-bold text-white transition-colors hover:opacity-90" style={{background:'#f68b2c'}}>
                    <CalendarPlus size={14}/> {t('userDetails.addLeave')}
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="flex items-center gap-1.5 text-[11px] text-gray-400 mb-3">
                  <Info size={12} className="flex-shrink-0"/> {t('userDetails.leaveListHint')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {sortedLeaveDays.map((l:any)=>{
                    const dateKey = (l.date ?? '').slice(0,10)
                    const isPast = dateKey && dateKey < todayISO
                    return (
                      <div key={l.id}
                        className={cn(
                          'flex items-center gap-2 pl-1.5 pr-1.5 py-1.5 rounded-xl border transition-colors',
                          isPast ? 'border-gray-100 bg-gray-50' : 'border-orange-100 bg-orange-50 hover:bg-orange-100',
                        )}>
                        <button type="button" disabled={isSchoolAdmin} onClick={()=>openEditLeave(l)}
                          title={t('userDetails.clickToEdit')}
                          className="flex items-center gap-2 disabled:cursor-default">
                          <span className={cn('w-7 h-7 rounded-lg flex items-center justify-center text-white flex-shrink-0', isPast && 'opacity-60')}
                            style={{background: isPast ? '#9ca3af' : '#f68b2c'}}>
                            {l.is_full_day_off ? <Sun size={13}/> : <Clock size={13}/>}
                          </span>
                          <span className="flex flex-col items-start leading-tight">
                            <span className={cn('text-xs font-bold', isPast ? 'text-gray-500' : 'text-gray-800')}>{fmtLeaveDate(l.date)}</span>
                            <span className={cn('text-[10px] font-medium', isPast ? 'text-gray-400' : 'text-orange-600')}>
                              {l.is_full_day_off ? t('userDetails.fullDayBadge') : `${fmt12(l.start_time)} – ${fmt12(l.end_time)}`}
                            </span>
                          </span>
                        </button>
                        {!isSchoolAdmin && (
                          <button type="button" onClick={()=>deleteLeaveDates([dateKey])}
                            disabled={leaveDeleting === dateKey}
                            aria-label={t('userDetails.deleteLeave')}
                            title={t('userDetails.deleteLeave')}
                            className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 flex-shrink-0">
                            <Trash2 size={13}/>
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Add/Edit Leave Day modal — POST admin/leave/create-update-leave ── */}
      {leaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-900">{leaveEditing ? t('userDetails.editLeave') : t('userDetails.addLeave')}</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {leaveEditing ? t('userDetails.editLeaveSubtitle') : t('userDetails.addLeaveSubtitle')}
                </p>
              </div>
              <button onClick={()=>setLeaveModal(false)}><X size={18} className="text-gray-400 hover:text-gray-600"/></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Dates */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">{t('userDetails.leaveDatesLabel')} *</label>
                {!leaveEditing && (
                  <>
                    <p className="text-[11px] text-gray-400 mb-2">{t('userDetails.leaveRangeHint')}</p>
                    <div className="flex items-end gap-2 mb-2">
                      <div className="flex-1">
                        <label className="text-[10px] font-semibold text-gray-400 block mb-0.5">{t('userDetails.leaveRangeFrom')}</label>
                        <input type="date" value={leaveRangeStart} onChange={e=>setLeaveRangeStart(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"/>
                      </div>
                      <div className="flex-1">
                        <label className="text-[10px] font-semibold text-gray-400 block mb-0.5">{t('userDetails.leaveRangeTo')}</label>
                        <input type="date" value={leaveRangeEnd} min={leaveRangeStart || undefined} onChange={e=>setLeaveRangeEnd(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"/>
                      </div>
                      <button onClick={addLeaveRange} disabled={!leaveRangeStart || !leaveRangeEnd}
                        className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-40 whitespace-nowrap" style={{background:'#f68b2c'}}>
                        <Plus size={13}/> {t('userDetails.addDate')}
                      </button>
                    </div>
                  </>
                )}
                {leaveDateList.length === 0 ? (
                  <p className="text-xs text-gray-400">{t('userDetails.noDatesAdded')}</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {leaveDateList.map(d=>(
                      <span key={d} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold">
                        {fmtLeaveDate(d)}
                        {!leaveEditing && (
                          <button onClick={()=>removeLeaveDate(d)} className="text-brand-400 hover:text-red-500"><X size={11}/></button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
                {!leaveEditing && leaveDateList.length > 1 && (
                  <button onClick={()=>setLeaveDateList([])} className="text-[11px] text-gray-400 hover:text-red-500 font-semibold mt-1.5">
                    {t('userDetails.clearAllDates')}
                  </button>
                )}
              </div>

              {/* Full day / specific hours */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-2">{t('userDetails.availabilityLabel')}</label>
                <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-gray-100 bg-gray-50">
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Sun size={15} className="text-gray-400"/> {t('userDetails.isFullDayOff')}
                  </span>
                  <Switch on={leaveFullDay} onClick={()=>setLeaveFullDay(v=>!v)}/>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  {leaveFullDay ? t('userDetails.fullDayHint') : t('userDetails.specificHoursHint')}
                </p>
              </div>

              {!leaveFullDay && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('userDetails.startTime')} *</label>
                    <input type="time" value={leaveStartTime} onChange={e=>setLeaveStartTime(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"/>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('userDetails.endTime')} *</label>
                    <input type="time" value={leaveEndTime} onChange={e=>setLeaveEndTime(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"/>
                  </div>
                </div>
              )}

              {leaveDateList.length > 0 && (
                <p className="flex items-center gap-1.5 text-[11px] text-brand-600 bg-brand-50 rounded-lg px-3 py-2">
                  <Info size={12} className="flex-shrink-0"/>
                  {t('userDetails.leaveSummary', { count: leaveDateList.length })}
                </p>
              )}
            </div>

            <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
              {leaveEditing && (
                <button onClick={()=>deleteLeaveDates([(leaveEditing.date ?? '').slice(0,10)])}
                  disabled={leaveDeleting === (leaveEditing.date ?? '').slice(0,10)}
                  className="py-2 px-3 border border-red-200 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5">
                  <Trash2 size={14}/> {leaveDeleting === (leaveEditing.date ?? '').slice(0,10) ? t('userDetails.deleting') : t('userDetails.deleteLeave')}
                </button>
              )}
              <button onClick={()=>setLeaveModal(false)}
                className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">{t('common.cancel')}</button>
              <button onClick={submitLeave} disabled={leaveSaving}
                className="flex-1 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{background:'#f68b2c'}}>
                {leaveSaving ? t('userDetails.saving') : (leaveEditing ? t('common.saveChanges') : t('userDetails.create'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ STUDENTS TAB ════════ */}
      {tab==='students' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900">
              {t('userDetails.studentsUnderTrainer',{count:studentMeta.total})}
            </h3>
          </div>
          {studentsLoading
            ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({length:6}).map((_,i)=><div key={i} className="h-72 bg-gray-100 rounded-2xl animate-pulse"/>)}
              </div>
            : students.length===0
              ? <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
                  <p className="text-sm font-medium" style={{color:'#f68b2c'}}>{t('userDetails.noStudentsUnderTrainer')}</p>
                </div>
              : <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {students.map((s:any)=>(
                      <StudentCard key={s.id} user={s} onView={()=>navigate(userPath(s.id))}/>
                    ))}
                  </div>
                  {studentMeta.total>studentPerPage&&(
                    <div className="bg-white rounded-xl border border-gray-100">
                      <Pagination page={studentPage} lastPage={studentMeta.last_page??1}
                        total={studentMeta.total} perPage={studentPerPage}
                        onPageChange={setStudentPage} onPerPageChange={n=>{setStudentPerPage(n);setStudentPage(1)}}/>
                    </div>
                  )}
                </>
          }
        </div>
      )}

      {/* ════════ BOOKINGS TAB ════════ */}
      {tab==='booking' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{t('userDetails.bookingsOverview')}</h2>
                <p className="text-sm text-gray-400 mt-0.5">{t('userDetails.bookingsCount',{count:bookingMeta.total||bookings.length})}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={bookingPayStatus} onChange={e=>{setBookingPayStatus(e.target.value);setBookingPage(1)}}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-600">
                  <option value="">{t('userDetails.paymentStatusFilter')}</option>
                  {['PAID','PENDING','FAILED','REFUNDED','PARTIALLY PAID'].map(s=><option key={s} value={s}>{statusLabel(t,s)}</option>)}
                </select>
                <select value={bookingPayMethod} onChange={e=>{setBookingPayMethod(e.target.value);setBookingPage(1)}}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-600">
                  <option value="">{t('userDetails.paymentMethodFilter')}</option>
                  {[{v:'CASH',k:'methodCash'},{v:'CARD',k:'methodCard'},{v:'WALLET',k:'methodWallet'},{v:'ONLINE',k:'methodOnline'}].map(m=><option key={m.v} value={m.v}>{t(`userDetails.${m.k}`)}</option>)}
                </select>
                {(bookingPayStatus||bookingPayMethod)&&(
                  <button onClick={()=>{setBookingPayStatus('');setBookingPayMethod('')}} className="text-xs text-red-500 font-semibold flex items-center gap-1"><X size={12}/> {t('common.clear')}</button>
                )}
              </div>
            </div>
          </div>
          {bookingsLoading
            ? <div className="space-y-3">{Array.from({length:5}).map((_,i)=><div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse"/>)}</div>
            : bookings.length===0
              ? <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center"><p className="text-gray-400 text-sm">{t('userDetails.noBookingsFound')}</p></div>
              : <>
                  <div className="space-y-3">
                    {bookings.map((b:any,i:number)=><BookingCard key={b.id??i} booking={b} onClick={()=>navigate(bookingPath(b.id))}
                      onTrainerClick={tid=>navigate(userPath(tid))}/>)}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100">
                    <Pagination page={bookingPage}
                      lastPage={bookingMeta.last_page??(Math.ceil((bookingMeta.total||bookings.length)/bookingPerPage)||1)}
                      total={bookingMeta.total||bookings.length} perPage={bookingPerPage}
                      onPageChange={setBookingPage} onPerPageChange={n=>{setBookingPerPage(n);setBookingPage(1)}}/>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      {label:t('userDetails.totalRevenueStat'),  val:<DirhamAmount value={totalRevenue} textClass="text-2xl font-bold text-white"/>, bg:'bg-gray-900'},
                      {label:t('userDetails.totalSessionsStat'), val:<span className="text-2xl font-bold text-white">{totalSessions}</span>, bg:'bg-gray-700'},
                      {label:t('userDetails.completedStat'),      val:<span className="text-2xl font-bold text-white">{totalDone}</span>,    bg:'bg-gray-500'},
                    ].map((s,i)=>(
                      <div key={i} className={cn('rounded-2xl p-4',s.bg)}>
                        <div className="text-xs font-bold text-gray-300 mb-1 uppercase tracking-wider">{s.label}</div>
                        {s.val}
                      </div>
                    ))}
                  </div>
                </>
          }
        </div>
      )}

      {/* ════════ BANK DETAILS TAB ════════ */}
      {tab==='bank-details' && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-4">{t('userDetails.bankDetailsTitle')}</h3>
          {bankDetails.length===0
            ? <p className="text-sm text-gray-400 text-center py-10">{t('userDetails.noBankDetails')}</p>
            : bankDetails.map((b:any,i:number)=>(
                <div key={i} className="p-4 border border-gray-100 rounded-xl mb-3">
                  <InfoRow label={t('userDetails.accountHolderName')} value={b.account_holder_name}/>
                  <InfoRow label={t('userDetails.bankName')}           value={b.bank_name}/>
                  <InfoRow label={t('userDetails.accountNumber')}      value={b.account_number}/>
                  <InfoRow label={t('userDetails.ibanNumber')}         value={b.iban_number}/>
                  <InfoRow label={t('common.status')}              value={b.is_active?t('common.active'):t('common.inactive')}/>
                </div>
              ))
          }
        </div>
      )}

      {/* ════════ USER DOCUMENTS TAB ════════ */}
      {tab==='user-document' && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">{t('userDetails.userDocumentsTitle')}</h3>
            <button onClick={()=>setDocumentModal({open:true,doc:null})}
              className="text-xs bg-brand-500 hover:bg-brand-600 text-white px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1">
              <Plus size={12}/> {t('userDetails.addDocument')}
            </button>
          </div>
          {docs.length===0
            ? <p className="text-sm text-gray-400 text-center py-10">{t('userDetails.noDocumentsUploaded')}</p>
            : <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {docs.map((doc:any,i:number)=>{
                  // API fields: doc_file (url), doc_type, doc_side, file_name, is_approved
                  const url   = doc.doc_file ?? doc.document_url
                  const label = [doc.doc_type ?? doc.document_type, doc.doc_side].filter(Boolean).join(' · ') || t('userDetails.documentFallback',{n:i+1})
                  return (
                  <div key={doc.id??i} className="border border-gray-100 rounded-xl overflow-hidden group">
                    <button onClick={()=>url && setDocPreview({...doc, url, label})} className="block w-full relative cursor-zoom-in" title={t('userDetails.clickToView')}>
                      {url && !isPdf(url)
                        ? <img src={url} alt={label} className="w-full h-40 object-cover"/>
                        : <div className="h-40 bg-gray-50 flex items-center justify-center"><FileText size={32} className="text-gray-300"/></div>
                      }
                      {url && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-white text-xs font-semibold bg-black/50 px-2 py-1 rounded-lg"><Eye size={13}/> {t('userDetails.viewHover')}</span>
                        </div>
                      )}
                      <div className="absolute top-2 end-2">
                        <span className={cn('px-1.5 py-0.5 rounded-full text-[11px] font-bold', doc.is_approved ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-700')}>
                          {doc.is_approved ? t('userDetails.approved') : t('userDetails.pending')}
                        </span>
                      </div>
                    </button>
                    <div className="px-3 py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-700 truncate">{label}</div>
                        <div className="text-[12px] text-gray-400">{doc.created_at?new Date(doc.created_at).toLocaleDateString():''}</div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button onClick={()=>setDocumentModal({open:true,doc})}
                          title={t('common.edit')} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600">
                          <Edit size={13}/>
                        </button>
                        {url && (
                          <button onClick={()=>downloadDoc(url, doc.file_name ?? doc.doc_type ?? `document-${i+1}`)}
                            title={t('common.download')} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600">
                            <Download size={13}/>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )})}
              </div>
          }
        </div>
      )}

      {/* ── Add / Edit Address modal ── */}
      <AddressModal
        open={addressModal.open}
        address={addressModal.address}
        userId={details?.id}
        onClose={()=>setAddressModal({open:false,address:null})}
        onSaved={()=>verifyQc.invalidateQueries({ queryKey: ['users', Number(id)] })}
      />

      {/* ── Add / Edit Document modal ── */}
      <DocumentModal
        open={documentModal.open}
        doc={documentModal.doc}
        userId={details?.id}
        onClose={()=>setDocumentModal({open:false,doc:null})}
        onSaved={()=>verifyQc.invalidateQueries({ queryKey: ['users', Number(id)] })}
      />

      {/* ── Document preview lightbox ── */}
      {docPreview && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{background:'rgba(0,0,0,0.85)'}} onClick={()=>setDocPreview(null)}>
          <div className="flex items-center justify-between px-5 py-3 text-white" onClick={e=>e.stopPropagation()}>
            <div className="text-sm font-semibold truncate">{docPreview.label ?? docPreview.doc_type ?? t('userDetails.documentFallbackTitle')}</div>
            <div className="flex items-center gap-2">
              <button onClick={()=>downloadDoc(docPreview.url, docPreview.file_name ?? docPreview.doc_type ?? 'document')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-semibold"><Download size={14}/> {t('common.download')}</button>
              <a href={docPreview.url} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-semibold"><Eye size={14}/> {t('userDetails.open')}</a>
              <button onClick={()=>setDocPreview(null)} className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={16}/></button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center p-4" onClick={e=>e.stopPropagation()}>
            {isPdf(docPreview.url)
              ? <iframe src={docPreview.url} title="document" className="w-full h-full bg-white rounded-lg"/>
              : <img src={docPreview.url} alt="document" className="max-w-full max-h-full object-contain rounded-lg"/>
            }
          </div>
        </div>
      )}
    </div>
  )
}
